import { EightExamples } from "./calcAgLengthyExamples";
export { EightExamples };

export const PARTIAL_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Natural domain",
    setup: "Find the natural domain of $f(x,y)=\\sqrt{9-x^2-y^2}$.",
    steps: [
      { text: "Require $9-x^2-y^2\\ge 0$.", why: "Carry the algebra carefully." },
      { text: "Rewrite $x^2+y^2\\le 9$.", why: "Carry the algebra carefully." },
      { text: "This is the closed disk of radius $3$.", why: "Carry the algebra carefully." },
      { text: "Interior points give a positive square root.", why: "Carry the algebra carefully." },
      { text: "On the boundary $f=0$.", why: "Carry the algebra carefully." },
      { text: "Outside, $f$ is not real-valued.", why: "Carry the algebra carefully." },
      { text: "Hence $D=\\{(x,y):x^2+y^2\\le 9\\}$.", why: "Carry the algebra carefully." },
      { text: "Sketch confirms a filled circle about the origin.", why: "Carry the algebra carefully." },
    ],
    result: "$D:\\,x^2+y^2\\le 9$",
    check: "$f(0,0)=3$.",
    mistake: "Omitting the boundary equality.",
    life: "Temperature maps $T(x,y)$, terrain $h(x,y)$, and brightness images are everyday functions of two variables \u2014 weather apps are literally contour maps of $z=f(x,y)$.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Level curves",
    setup: "Describe level curves of $f(x,y)=x^2+y^2$ for $c=1,4,9$.",
    steps: [
      { text: "Set $x^2+y^2=c$.", why: "Carry the algebra carefully." },
      { text: "$c=1$ is the unit circle.", why: "Carry the algebra carefully." },
      { text: "$c=4$ has radius $2$.", why: "Carry the algebra carefully." },
      { text: "$c=9$ has radius $3$.", why: "Carry the algebra carefully." },
      { text: "Larger $c$ sits higher on the paraboloid.", why: "Carry the algebra carefully." },
      { text: "Distinct levels never intersect.", why: "Carry the algebra carefully." },
      { text: "Contours are concentric circles.", why: "Carry the algebra carefully." },
      { text: "This is the contour map of a circular bowl.", why: "Carry the algebra carefully." },
    ],
    result: "Circles of radius $\\sqrt{c}$",
    check: "$f(1,0)=1$ lies on $c=1$.",
    mistake: "Drawing ellipses instead of circles.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "Partial $f_x$",
    setup: "If $f(x,y)=x^2 y+e^y$, compute $f_x$.",
    steps: [
      { text: "Hold $y$ fixed.", why: "Carry the algebra carefully." },
      { text: "$(x^2 y)_x=2xy$.", why: "Carry the algebra carefully." },
      { text: "$(e^y)_x=0$.", why: "Carry the algebra carefully." },
      { text: "So $f_x=2xy$.", why: "Carry the algebra carefully." },
      { text: "Sample: $f_x(1,2)=4$.", why: "Carry the algebra carefully." },
      { text: "Notation: $f_x=\\partial f/\\partial x$.", why: "Carry the algebra carefully." },
      { text: "Later check with mixed partials.", why: "Carry the algebra carefully." },
      { text: "Report the simplified expression.", why: "Carry the algebra carefully." },
    ],
    result: "$f_x=2xy$",
    check: "$f_{xx}=2y$.",
    mistake: "Differentiating $e^y$ with respect to $x$.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "Partial $f_y$",
    setup: "For the same $f$, compute $f_y$.",
    steps: [
      { text: "Hold $x$ fixed.", why: "Carry the algebra carefully." },
      { text: "$(x^2 y)_y=x^2$.", why: "Carry the algebra carefully." },
      { text: "$(e^y)_y=e^y$.", why: "Carry the algebra carefully." },
      { text: "$f_y=x^2+e^y$.", why: "Carry the algebra carefully." },
      { text: "At $(0,0)$: $f_y=1$.", why: "Carry the algebra carefully." },
      { text: "Gradient uses both partials.", why: "Carry the algebra carefully." },
      { text: "Verify by expanding.", why: "Carry the algebra carefully." },
      { text: "Keep $e^y$ unsimplified.", why: "Carry the algebra carefully." },
    ],
    result: "$f_y=x^2+e^y$",
    check: "$f_{yx}=2x$.",
    mistake: "Dropping the $e^y$ term.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "Clairaut check",
    setup: "Verify $f_{xy}=f_{yx}$ for $f=x^2 y+e^y$.",
    steps: [
      { text: "$f_x=2xy$ so $f_{xy}=2x$.", why: "Carry the algebra carefully." },
      { text: "$f_y=x^2+e^y$ so $f_{yx}=2x$.", why: "Carry the algebra carefully." },
      { text: "Both equal $2x$.", why: "Carry the algebra carefully." },
      { text: "Equality holds on $\\mathbb{R}^2$.", why: "Carry the algebra carefully." },
      { text: "Clairaut applies because $f$ is $C^2$.", why: "Carry the algebra carefully." },
      { text: "Order of mixed partials is free.", why: "Carry the algebra carefully." },
      { text: "Use this as a debugging check.", why: "Carry the algebra carefully." },
      { text: "State equality explicitly.", why: "Carry the algebra carefully." },
    ],
    result: "$f_{xy}=f_{yx}=2x$",
    check: "Both computation routes match.",
    mistake: "Swapping order without smoothness.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Gradient vector",
    setup: "Find $\\nabla f(1,0)$ for $f=x^2 y+e^y$.",
    steps: [
      { text: "$\\nabla f=\\langle f_x,f_y\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$f_x=2xy$, $f_y=x^2+e^y$.", why: "Carry the algebra carefully." },
      { text: "At $(1,0)$: $f_x=0$, $f_y=2$.", why: "Carry the algebra carefully." },
      { text: "$\\nabla f(1,0)=\\langle 0,2\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Steepest ascent is in the $+y$ direction.", why: "Carry the algebra carefully." },
      { text: "Magnitude equals $2$.", why: "Carry the algebra carefully." },
      { text: "Unit direction $\\langle 0,1\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Gradient is orthogonal to the level curve.", why: "Carry the algebra carefully." },
    ],
    result: "$\\langle 0,2\\rangle$",
    check: "Dot product with a level-curve tangent is $0$.",
    mistake: "Evaluating at the wrong point.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Linearization",
    setup: "Approximate $f(1.1,0.05)$ using the linearization at $(1,0)$.",
    steps: [
      { text: "$f(1,0)=1$.", why: "Carry the algebra carefully." },
      { text: "$\\nabla f(1,0)=\\langle 0,2\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$L(x,y)=1+2y$.", why: "Carry the algebra carefully." },
      { text: "$L(1.1,0.05)=1.1$.", why: "Carry the algebra carefully." },
      { text: "True value $\\approx 1.112$.", why: "Carry the algebra carefully." },
      { text: "Absolute error $\\approx 0.012$.", why: "Carry the algebra carefully." },
      { text: "Relative error is small.", why: "Carry the algebra carefully." },
      { text: "Linear models local weather/terrain slopes the same way.", why: "Carry the algebra carefully." },
    ],
    result: "$L\\approx 1.1$",
    check: "True and linear agree to about $0.01$.",
    mistake: "Using the wrong base point.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Tangent plane",
    setup: "Tangent plane to $z=x^2+y^2$ at $(1,1,2)$.",
    steps: [
      { text: "$f_x=2x$, $f_y=2y$.", why: "Carry the algebra carefully." },
      { text: "At $(1,1)$: both equal $2$.", why: "Carry the algebra carefully." },
      { text: "$z-2=2(x-1)+2(y-1)$.", why: "Carry the algebra carefully." },
      { text: "$z=2x+2y-2$.", why: "Carry the algebra carefully." },
      { text: "Passes through $(1,1,2)$.", why: "Carry the algebra carefully." },
      { text: "Normal $\\langle 2,2,-1\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Surface is a circular paraboloid.", why: "Carry the algebra carefully." },
      { text: "Plane is the best local flat model.", why: "Carry the algebra carefully." },
    ],
    result: "$z=2x+2y-2$",
    check: "Substitute $(1,1)$ recovers $z=2$.",
    mistake: "Forgetting the $(x-x_0)$ shifts.",
  },
];

export const PARTIAL_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Chain rule",
    setup: "$z=x^2 y$, $x=t$, $y=e^t$. Find $dz/dt$.",
    steps: [
      { text: "$z_x=2xy$, $z_y=x^2$.", why: "Carry the algebra carefully." },
      { text: "$dx/dt=1$, $dy/dt=e^t$.", why: "Carry the algebra carefully." },
      { text: "$dz/dt=z_x x'+z_y y'$.", why: "Carry the algebra carefully." },
      { text: "$=2xy\\cdot 1+x^2 e^t$.", why: "Carry the algebra carefully." },
      { text: "Substitute $x=t$, $y=e^t$.", why: "Carry the algebra carefully." },
      { text: "$dz/dt=2t e^t+t^2 e^t$.", why: "Carry the algebra carefully." },
      { text: "Factor $e^t(2t+t^2)$.", why: "Carry the algebra carefully." },
      { text: "Direct substitute $z=t^2 e^t$ and differentiate to check.", why: "Carry the algebra carefully." },
    ],
    result: "$e^t(t^2+2t)$",
    check: "Product rule on $t^2 e^t$ matches.",
    mistake: "Forgetting one chain term.",
    life: "Machine-learning gradients, finance Greeks, and weather sensitivity all ask the same question as Part 2: how does a multivariable output change when one input shifts.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Directional derivative",
    setup: "$f=x^2+y^2$ at $(1,1)$ in direction $\\mathbf{u}=\\langle 3/5,4/5\\rangle$.",
    steps: [
      { text: "$\\nabla f=\\langle 2x,2y\\rangle$.", why: "Carry the algebra carefully." },
      { text: "At $(1,1)$: $\\langle 2,2\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$\\mathbf{u}$ is already a unit vector.", why: "Carry the algebra carefully." },
      { text: "$D_{\\mathbf{u}}f=\\nabla f\\cdot\\mathbf{u}$.", why: "Carry the algebra carefully." },
      { text: "$=2\\cdot 3/5+2\\cdot 4/5$.", why: "Carry the algebra carefully." },
      { text: "$=6/5+8/5=14/5$.", why: "Carry the algebra carefully." },
      { text: "Positive means $f$ increases that way.", why: "Carry the algebra carefully." },
      { text: "Max rate is $|\\nabla f|=2\\sqrt{2}$.", why: "Carry the algebra carefully." },
    ],
    result: "$14/5$",
    check: "$|\\mathbf{u}|=1$ confirmed.",
    mistake: "Using a non-unit direction vector.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "Critical points",
    setup: "Find critical points of $f(x,y)=x^2+y^2-2x$.",
    steps: [
      { text: "$f_x=2x-2$, $f_y=2y$.", why: "Carry the algebra carefully." },
      { text: "Set $f_x=0\\Rightarrow x=1$.", why: "Carry the algebra carefully." },
      { text: "Set $f_y=0\\Rightarrow y=0$.", why: "Carry the algebra carefully." },
      { text: "Only critical point $(1,0)$.", why: "Carry the algebra carefully." },
      { text: "$f(1,0)=-1$.", why: "Carry the algebra carefully." },
      { text: "Hessian later classifies it.", why: "Carry the algebra carefully." },
      { text: "No other solutions.", why: "Carry the algebra carefully." },
      { text: "Complete the square: $f=(x-1)^2+y^2-1$.", why: "Carry the algebra carefully." },
    ],
    result: "$(1,0)$",
    check: "Completed-square confirms a minimum.",
    mistake: "Missing $f_y=0$.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "Second derivative test",
    setup: "Classify $(1,0)$ for $f=x^2+y^2-2x$.",
    steps: [
      { text: "$f_{xx}=2$, $f_{yy}=2$, $f_{xy}=0$.", why: "Carry the algebra carefully." },
      { text: "$D=f_{xx}f_{yy}-f_{xy}^2=4$.", why: "Carry the algebra carefully." },
      { text: "$D>0$ and $f_{xx}>0$.", why: "Carry the algebra carefully." },
      { text: "Local minimum.", why: "Carry the algebra carefully." },
      { text: "Value $f(1,0)=-1$.", why: "Carry the algebra carefully." },
      { text: "Matches completed square.", why: "Carry the algebra carefully." },
      { text: "No saddle here.", why: "Carry the algebra carefully." },
      { text: "State the test conclusion clearly.", why: "Carry the algebra carefully." },
    ],
    result: "Local min of $-1$",
    check: "$D=4>0$, $f_{xx}>0$.",
    mistake: "Misreading the saddle case $D<0$.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "Constrained preview",
    setup: "Minimize $f=x^2+y^2$ on $x+y=1$ by substitution.",
    steps: [
      { text: "Set $y=1-x$.", why: "Carry the algebra carefully." },
      { text: "$g(x)=x^2+(1-x)^2$.", why: "Carry the algebra carefully." },
      { text: "$g'=2x-2(1-x)=4x-2$.", why: "Carry the algebra carefully." },
      { text: "$g'=0\\Rightarrow x=1/2$.", why: "Carry the algebra carefully." },
      { text: "$y=1/2$.", why: "Carry the algebra carefully." },
      { text: "$f=1/2$.", why: "Carry the algebra carefully." },
      { text: "Second derivative $g''=4>0$.", why: "Carry the algebra carefully." },
      { text: "Same point Lagrange would find.", why: "Carry the algebra carefully." },
    ],
    result: "$(1/2,1/2)$, value $1/2$",
    check: "Geometry: closest point on the line to origin.",
    mistake: "Stopping at critical $x$ without $y$.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Total differential",
    setup: "$z=xy$, estimate $\\Delta z$ from $(2,3)$ with $dx=0.1$, $dy=-0.2$.",
    steps: [
      { text: "$dz=y\\,dx+x\\,dy$.", why: "Carry the algebra carefully." },
      { text: "At $(2,3)$: $dz=3(0.1)+2(-0.2)$.", why: "Carry the algebra carefully." },
      { text: "$=0.3-0.4=-0.1$.", why: "Carry the algebra carefully." },
      { text: "True $\\Delta z=2.1\\cdot 2.8-6=-0.12$.", why: "Carry the algebra carefully." },
      { text: "Error $0.02$.", why: "Carry the algebra carefully." },
      { text: "Differential approximates small changes.", why: "Carry the algebra carefully." },
      { text: "Used in error propagation.", why: "Carry the algebra carefully." },
      { text: "Report $dz\\approx -0.1$.", why: "Carry the algebra carefully." },
    ],
    result: "$dz\\approx -0.1$",
    check: "True change near the estimate.",
    mistake: "Swapping $x$ and $y$ in $dz$.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Max on closed set",
    setup: "$f=x+y$ on the triangle $x\\ge 0,y\\ge 0,x+y\\le 1$.",
    steps: [
      { text: "Interior: $\\nabla f=\\langle 1,1\\rangle\\neq\\mathbf{0}$ \u2014 no interior crit.", why: "Carry the algebra carefully." },
      { text: "Edges: $y=0$, $f=x$ max $1$ at $(1,0)$.", why: "Carry the algebra carefully." },
      { text: "$x=0$, $f=y$ max $1$ at $(0,1)$.", why: "Carry the algebra carefully." },
      { text: "$x+y=1$, $f=1$ constantly.", why: "Carry the algebra carefully." },
      { text: "Compare values $0$ and $1$.", why: "Carry the algebra carefully." },
      { text: "Maximum $1$ on the hypotenuse.", why: "Carry the algebra carefully." },
      { text: "Minimum $0$ at origin.", why: "Carry the algebra carefully." },
      { text: "Extreme Value Theorem applies.", why: "Carry the algebra carefully." },
    ],
    result: "Max $1$, min $0$",
    check: "Boundary scan is complete.",
    mistake: "Looking only at interior.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Gradient geometry",
    setup: "Show $\\nabla f$ is normal to the level curve of $f=x^2-y$ at $(2,3)$.",
    steps: [
      { text: "Level: $x^2-y=1$ since $f(2,3)=1$.", why: "Carry the algebra carefully." },
      { text: "Implicit: $2x-y'=0$ so slope $y'=2x$.", why: "Carry the algebra carefully." },
      { text: "At $x=2$, tangent slope $4$, direction $\\langle 1,4\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$\\nabla f=\\langle 2x,-1\\rangle=\\langle 4,-1\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Dot: $4\\cdot 1+(-1)\\cdot 4=0$.", why: "Carry the algebra carefully." },
      { text: "Orthogonal confirmed.", why: "Carry the algebra carefully." },
      { text: "Same fact used in Lagrange.", why: "Carry the algebra carefully." },
      { text: "State normality clearly.", why: "Carry the algebra carefully." },
    ],
    result: "$\\nabla f\\cdot\\mathbf{T}=0$",
    check: "Dot product vanishes.",
    mistake: "Using a non-tangent vector.",
  },
];

export const VECTOR_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Velocity of a helix",
    setup: "$\\mathbf{r}(t)=\\langle\\cos t,\\sin t,t\\rangle$. Find $\\mathbf{r}'(0)$ and speed.",
    steps: [
      { text: "$\\mathbf{r}'(t)=\\langle -\\sin t,\\cos t,1\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$\\mathbf{r}'(0)=\\langle 0,1,1\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Speed $|\\mathbf{r}'|=\\sqrt{2}$ constant.", why: "Carry the algebra carefully." },
      { text: "Unit tangent $\\mathbf{T}=\\langle 0,1/\\sqrt{2},1/\\sqrt{2}\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Path is a helix.", why: "Carry the algebra carefully." },
      { text: "Differentiate componentwise.", why: "Carry the algebra carefully." },
      { text: "Check $|\\mathbf{T}|=1$.", why: "Carry the algebra carefully." },
      { text: "Report velocity and speed.", why: "Carry the algebra carefully." },
    ],
    result: "$\\langle 0,1,1\\rangle$, speed $\\sqrt{2}$",
    check: "$|\\mathbf{T}|=1$.",
    mistake: "Forgetting the $z$-component derivative.",
    life: "GPS trackers, drone controllers, and roller-coaster design all use $\\mathbf{r}(t)$ and work integrals $\\int_C\\mathbf{F}\\cdot d\\mathbf{r}$ exactly as in this part.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Arc-length element",
    setup: "For $\\mathbf{r}(t)=\\langle t,t^2\\rangle$, $t\\in[0,1]$, write $\\int_C ds$.",
    steps: [
      { text: "$\\mathbf{r}'=\\langle 1,2t\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$|\\mathbf{r}'|=\\sqrt{1+4t^2}$.", why: "Carry the algebra carefully." },
      { text: "$\\int_C ds=\\int_0^1\\sqrt{1+4t^2}\\,dt$.", why: "Carry the algebra carefully." },
      { text: "This is curve length.", why: "Carry the algebra carefully." },
      { text: "No closed form needed here.", why: "Carry the algebra carefully." },
      { text: "Setup is the exam skill.", why: "Carry the algebra carefully." },
      { text: "Verify endpoints $(0,0)$ to $(1,1)$.", why: "Carry the algebra carefully." },
      { text: "Leave the integral ready.", why: "Carry the algebra carefully." },
    ],
    result: "$\\int_0^1\\sqrt{1+4t^2}\\,dt$",
    check: "Endpoints match the parabola.",
    mistake: "Dropping the speed factor.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "Work along a line",
    setup: "$\\mathbf{F}=\\langle y,x\\rangle$ along $\\mathbf{r}(t)=\\langle t,t\\rangle$, $t:0\\to 1$.",
    steps: [
      { text: "$\\mathbf{r}'=\\langle 1,1\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$\\mathbf{F}(\\mathbf{r})=\\langle t,t\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Dot product $2t$.", why: "Carry the algebra carefully." },
      { text: "$W=\\int_0^1 2t\\,dt=1$.", why: "Carry the algebra carefully." },
      { text: "Path is the segment $y=x$.", why: "Carry the algebra carefully." },
      { text: "Conservative check later.", why: "Carry the algebra carefully." },
      { text: "Positive work means field helps.", why: "Carry the algebra carefully." },
      { text: "State $W=1$.", why: "Carry the algebra carefully." },
    ],
    result: "$W=1$",
    check: "Direct integration checks out.",
    mistake: "Using $\\mathbf{F}\\cdot\\mathbf{r}$ instead of $\\mathbf{r}'$.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "Conservative test",
    setup: "Is $\\mathbf{F}=\\langle 2xy,x^2+1\\rangle$ conservative?",
    steps: [
      { text: "$M=2xy$, $N=x^2+1$.", why: "Carry the algebra carefully." },
      { text: "$M_y=2x$, $N_x=2x$.", why: "Carry the algebra carefully." },
      { text: "Equal, so yes (on $\\mathbb{R}^2$).", why: "Carry the algebra carefully." },
      { text: "Find $f$ with $f_x=M$.", why: "Carry the algebra carefully." },
      { text: "$f=x^2 y+g(y)$.", why: "Carry the algebra carefully." },
      { text: "$f_y=x^2+g'=N\\Rightarrow g'=1$.", why: "Carry the algebra carefully." },
      { text: "$f=x^2 y+y$.", why: "Carry the algebra carefully." },
      { text: "Verify $\\nabla f=\\mathbf{F}$.", why: "Carry the algebra carefully." },
    ],
    result: "Yes; $f=x^2 y+y$",
    check: "$\\nabla f$ recovers $\\mathbf{F}$.",
    mistake: "Stopping after the cross-partial test.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "FTC for line integrals",
    setup: "Using $f=x^2 y+y$, evaluate $\\int_C\\mathbf{F}\\cdot d\\mathbf{r}$ from $(0,0)$ to $(2,3)$.",
    steps: [
      { text: "Field is conservative.", why: "Carry the algebra carefully." },
      { text: "Integral $=f(2,3)-f(0,0)$.", why: "Carry the algebra carefully." },
      { text: "$f(2,3)=12+3=15$.", why: "Carry the algebra carefully." },
      { text: "$f(0,0)=0$.", why: "Carry the algebra carefully." },
      { text: "Answer $15$.", why: "Carry the algebra carefully." },
      { text: "Path independence applies.", why: "Carry the algebra carefully." },
      { text: "No parameterization needed.", why: "Carry the algebra carefully." },
      { text: "Closed loops give $0$.", why: "Carry the algebra carefully." },
    ],
    result: "$15$",
    check: "Any path from start to end agrees.",
    mistake: "Still parameterizing unnecessarily.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Flux setup",
    setup: "Flux of $\\mathbf{F}=\\langle x,y\\rangle$ across unit circle (outward).",
    steps: [
      { text: "Parameterize $x=\\cos t$, $y=\\sin t$.", why: "Carry the algebra carefully." },
      { text: "Use $\\oint M\\,dy-N\\,dx$.", why: "Carry the algebra carefully." },
      { text: "Integrand becomes $1$.", why: "Carry the algebra carefully." },
      { text: "$\\int_0^{2\\pi}1\\,dt=2\\pi$.", why: "Carry the algebra carefully." },
      { text: "Field radiates outward.", why: "Carry the algebra carefully." },
      { text: "Matches divergence theorem preview.", why: "Carry the algebra carefully." },
      { text: "Orientation counterclockwise.", why: "Carry the algebra carefully." },
      { text: "Report flux $2\\pi$.", why: "Carry the algebra carefully." },
    ],
    result: "$2\\pi$",
    check: "Divergence is $2$, area $\\pi$, product $2\\pi$.",
    mistake: "Clockwise orientation sign error.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Circulation intuition",
    setup: "Explain $\\oint_C\\mathbf{F}\\cdot d\\mathbf{r}$ for a whirlpool field.",
    steps: [
      { text: "Circulation measures net tangential push around a loop.", why: "Carry the algebra carefully." },
      { text: "Positive if field spins with the walk.", why: "Carry the algebra carefully." },
      { text: "Negative if against.", why: "Carry the algebra carefully." },
      { text: "Zero if no net spin.", why: "Carry the algebra carefully." },
      { text: "Green relates it to curl.", why: "Carry the algebra carefully." },
      { text: "Closed path required.", why: "Carry the algebra carefully." },
      { text: "Orientation matters.", why: "Carry the algebra carefully." },
      { text: "State the physical reading.", why: "Carry the algebra carefully." },
    ],
    result: "Net spin score around $C$",
    check: "Matches pinwheel intuition.",
    mistake: "Confusing flux with circulation.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Potential reconstruction",
    setup: "Find potential for $\\mathbf{F}=\\langle e^y, x e^y\\rangle$.",
    steps: [
      { text: "$M_y=e^y=N_x$ \u2014 conservative.", why: "Carry the algebra carefully." },
      { text: "Integrate $M$: $f=x e^y+g(y)$.", why: "Carry the algebra carefully." },
      { text: "$f_y=x e^y+g'=N=x e^y$.", why: "Carry the algebra carefully." },
      { text: "$g'=0$.", why: "Carry the algebra carefully." },
      { text: "$f=x e^y+C$.", why: "Carry the algebra carefully." },
      { text: "Check $\\nabla f=\\mathbf{F}$.", why: "Carry the algebra carefully." },
      { text: "Domain $\\mathbb{R}^2$ simply connected.", why: "Carry the algebra carefully." },
      { text: "Report $f=x e^y$.", why: "Carry the algebra carefully." },
    ],
    result: "$f=x e^y$",
    check: "Gradient check passes.",
    mistake: "Adding an illegal $x$-only leftover.",
  },
];

export const VECTOR_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Green shortcut",
    setup: "Evaluate $\\oint_C(x^2-y)\\,dx+(x+y^2)\\,dy$ on $x^2+y^2=4$, counterclockwise.",
    steps: [
      { text: "The disk has radius 2 and the boundary is counterclockwise.", why: "Positive orientation fixes the sign." },
      { text: "$M=x^2-y$ and $N=x+y^2$ are polynomials.", why: "Their first derivatives are continuous on a neighborhood of the closed disk." },
      { text: "$N_x=1$ and $M_y=-1$.", why: "Differentiate each component in the required variable." },
      { text: "$N_x-M_y=2$.", why: "Circulation uses scalar curl rather than divergence." },
      { text: "$\\oint_C M\\,dx+N\\,dy=\\iint_R2\\,dA$.", why: "Apply Green after checking its assumptions." },
      { text: "$\\iint_R2\\,dA=\\int_0^{2\\pi}\\int_0^2 2r\\,dr\\,d\\theta$.", why: "Polar area contributes the Jacobian r." },
      { text: "$\\int_0^2 2r\\,dr=4$; hence the outer integral is $8\\pi$.", why: "Evaluate both definite integrals." },
      { text: "On $(x,y)=(2\\cos t,2\\sin t)$, the boundary integrand is $4-8\\cos^2t\\sin t+8\\sin^2t\\cos t$. Its last two terms integrate to zero.", why: "Direct parameterization independently confirms the result." },
    ],
    result: "$8\\pi$",
    check: "Direct curl integral matches.",
    mistake: "Using radius $2$ as area.",
    life: "Green's Theorem is the same boundary-to-interior swap CAD tools use for planar area and flow balance from edge measurements alone.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Area via Green",
    setup: "For $a,b>0$, derive the area of the ellipse $x^2/a^2+y^2/b^2=1$.",
    steps: [
      { text: "Choose $M=-y/2$ and $N=x/2$.", why: "This smooth auxiliary field turns circulation into area." },
      { text: "$N_x-M_y=1/2-(-1/2)=1$.", why: "The double integral of 1 is the enclosed area." },
      { text: "$A=\\tfrac12\\oint_C(x\\,dy-y\\,dx)$.", why: "Use positive boundary orientation." },
      { text: "Set $x=a\\cos t$, $y=b\\sin t$, $0\\le t\\le2\\pi$.", why: "Positive a and b make this traversal counterclockwise." },
      { text: "$dx=-a\\sin t\\,dt$, $dy=b\\cos t\\,dt$.", why: "Differentiate the parameterization." },
      { text: "$x\\,dy-y\\,dx=ab(\\cos^2t+\\sin^2t)\\,dt=ab\\,dt$.", why: "The negative sign on dx becomes a plus." },
      { text: "$A=\\tfrac12\\int_0^{2\\pi}ab\\,dt=\\pi ab$.", why: "Keep the one-half factor." },
      { text: "When $a=b=r$, the result is $\\pi r^2$; reversing the traversal gives $-\\pi ab$.", why: "Check the circle limit and signed-area convention." },
    ],
    result: "$\\pi ab$",
    check: "Specializes to circle formula.",
    mistake: "Missing the $1/2$ factor.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "Surface area $z=g$",
    setup: "Area of $z=x^2+y^2$ above $x^2+y^2\\le 1$.",
    steps: [
      { text: "$g_x=2x$, $g_y=2y$.", why: "Carry the algebra carefully." },
      { text: "Factor $\\sqrt{1+4x^2+4y^2}$.", why: "Carry the algebra carefully." },
      { text: "Polar: $\\int_0^{2\\pi}\\int_0^1\\sqrt{1+4r^2}\\,r\\,dr\\,d\\theta$.", why: "Carry the algebra carefully." },
      { text: "$u=1+4r^2$, $du=8r\\,dr$.", why: "Carry the algebra carefully." },
      { text: "Get $\\pi(5\\sqrt{5}-1)/6$.", why: "Carry the algebra carefully." },
      { text: "Tilt factor $>1$.", why: "Carry the algebra carefully." },
      { text: "Shadow is unit disk.", why: "Carry the algebra carefully." },
      { text: "Report exact value.", why: "Carry the algebra carefully." },
    ],
    result: "$\\pi(5\\sqrt{5}-1)/6$",
    check: "$u$-sub limits $1\\to 5$.",
    mistake: "Forgetting the Jacobian $r$.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "Parametric surface element",
    setup: "Write $dS$ for $\\mathbf{r}(u,v)=\\langle u\\cos v,u\\sin v,u\\rangle$.",
    steps: [
      { text: "$\\mathbf{r}_u=\\langle\\cos v,\\sin v,1\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$\\mathbf{r}_v=\\langle -u\\sin v,u\\cos v,0\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Cross product magnitude $\\sqrt{2}\\,u$ (for $u>0$).", why: "Carry the algebra carefully." },
      { text: "$dS=\\sqrt{2}\\,u\\,du\\,dv$.", why: "Carry the algebra carefully." },
      { text: "Cone surface.", why: "Carry the algebra carefully." },
      { text: "Normal from cross product.", why: "Carry the algebra carefully." },
      { text: "Keep absolute value.", why: "Carry the algebra carefully." },
      { text: "State $dS$.", why: "Carry the algebra carefully." },
    ],
    result: "$dS=\\sqrt{2}\\,u\\,du\\,dv$",
    check: "Check dimensionally.",
    mistake: "Dropping the $u$ factor.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "Green vs path independence",
    setup: "Let $\\mathbf F=(M,N)$ be continuously differentiable on an open simply connected domain, with $N_x-M_y=0$. Explain path independence.",
    steps: [
      { text: "Take a piecewise-smooth simple closed curve in the domain.", why: "Simple connectedness ensures that its interior remains in the domain." },
      { text: "Green gives $\\oint_C\\mathbf F\\cdot d\\mathbf r=\\iint_R0\\,dA=0$.", why: "Smoothness holds throughout the enclosed region." },
      { text: "Closed paths that split into finitely many simple loops also have integral zero.", why: "Add the loop integrals with their traversal orientations." },
      { text: "Take two paths from a fixed point A to B. Follow the first and reverse the second.", why: "This constructs a closed path." },
      { text: "$\\int_{C_1}\\mathbf F\\cdot d\\mathbf r-\\int_{C_2}\\mathbf F\\cdot d\\mathbf r=0$.", why: "Reversing a path negates its integral." },
      { text: "Define $f(B)=\\int_A^B\\mathbf F\\cdot d\\mathbf r$.", why: "Path independence makes this definition unambiguous." },
      { text: "Adding a short horizontal or vertical segment gives $f_x=M$ and $f_y=N$ by continuity.", why: "Thus the field is the gradient of a potential." },
      { text: "On the punctured plane, $(-y/r^2,x/r^2)$ has zero curl but unit-circle circulation $2\\pi$.", why: "The domain assumption cannot be dropped." },
    ],
    result: "Conservative / path-independent",
    check: "Closed integrals vanish.",
    mistake: "Ignoring domain topology.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Orientation warning",
    setup: "Evaluate $\\oint_C(-y\\,dx+x\\,dy)$ on the unit circle traversed clockwise.",
    steps: [
      { text: "$M=-y$, $N=x$ are smooth on the full disk.", why: "The regularity assumption holds." },
      { text: "$N_x-M_y=1-(-1)=2$.", why: "Use the circulation form." },
      { text: "The positively oriented disk gives $\\iint_R2\\,dA=2\\pi$.", why: "This value corresponds to counterclockwise traversal." },
      { text: "The requested clockwise circulation is $-2\\pi$.", why: "Reverse the orientation once." },
      { text: "For a direct check, take $x=\\cos t$, $y=-\\sin t$, $0\\le t\\le2\\pi$.", why: "The path leaves (1,0) moving downward." },
      { text: "$dx=-\\sin t\\,dt$, $dy=-\\cos t\\,dt$.", why: "Differentiate both components with their signs." },
      { text: "$-y\\,dx+x\\,dy=-(\\sin^2t+\\cos^2t)\\,dt=-dt$.", why: "Substitute the clockwise parameterization." },
      { text: "$\\int_0^{2\\pi}-dt=-2\\pi$.", why: "The direct integral agrees with the sign-adjusted Green result." },
    ],
    result: "$-2\\pi$",
    check: "Walk-with-region-on-left rule.",
    mistake: "Ignoring orientation entirely.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Flux-divergence form",
    setup: "Find the outward flux of $\\mathbf F=(x^2,y)$ across $[0,1]\\times[0,2]$.",
    steps: [
      { text: "The rectangular boundary is piecewise smooth and the field is polynomial.", why: "Corners are allowed; all regularity conditions hold." },
      { text: "$M_x+N_y=2x+1$.", why: "Outward flux uses divergence." },
      { text: "Flux $=\\int_0^1\\int_0^2(2x+1)\\,dy\\,dx$.", why: "Integrate over the enclosed rectangle." },
      { text: "$\\int_0^2(2x+1)\\,dy=4x+2$.", why: "The vertical interval has length 2." },
      { text: "$\\int_0^1(4x+2)\\,dx=[2x^2+2x]_0^1=4$.", why: "Finish the area integral." },
      { text: "Right edge: $\\mathbf n=(1,0)$, so flux is $\\int_0^2 1\\,dy=2$.", why: "Check independently using outward normals." },
      { text: "Top edge: $\\mathbf n=(0,1)$, so flux is $\\int_0^1 2\\,dx=2$.", why: "The top component is y=2." },
      { text: "The left and bottom contributions are zero; the total is 2+2=4.", why: "The boundary computation matches the divergence integral." },
    ],
    result: "Outward flux $=4$",
    check: "Matches physical balance.",
    mistake: "Swapping $M$ and $N$ signs.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Sphere area check",
    setup: "Outline proving sphere area $4\\pi a^2$ via $z=\\sqrt{a^2-x^2-y^2}$.",
    steps: [
      { text: "Upper hemisphere over disk radius $a$.", why: "Carry the algebra carefully." },
      { text: "Compute tilt factor $a/\\sqrt{a^2-r^2}$.", why: "Carry the algebra carefully." },
      { text: "Polar integral yields $2\\pi a^2$.", why: "Carry the algebra carefully." },
      { text: "Double for full sphere.", why: "Carry the algebra carefully." },
      { text: "$4\\pi a^2$.", why: "Carry the algebra carefully." },
      { text: "Classic formula recovered.", why: "Carry the algebra carefully." },
      { text: "Singularity at equator handled carefully.", why: "Carry the algebra carefully." },
      { text: "State final area.", why: "Carry the algebra carefully." },
    ],
    result: "$4\\pi a^2$",
    check: "Matches textbook formula.",
    mistake: "Forgetting to double the hemisphere.",
  },
];

export const INTEGRALS_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Rectangle double integral",
    setup: "$\\iint_R (2x+3y)\\,dA$, $R=[0,1]\\\\times[0,2]$.",
    steps: [
      { text: "Iterate $\\int_0^1\\int_0^2(2x+3y)\\,dy\\,dx$.", why: "Carry the algebra carefully." },
      { text: "Inner: $2xy+\\tfrac32 y^2|_0^2=4x+6$.", why: "Carry the algebra carefully." },
      { text: "Outer: $\\int_0^1(4x+6)\\,dx=2+6=8$.", why: "Carry the algebra carefully." },
      { text: "Fubini allows $dx\\,dy$ too.", why: "Carry the algebra carefully." },
      { text: "Same answer either order.", why: "Carry the algebra carefully." },
      { text: "Check dimensions.", why: "Carry the algebra carefully." },
      { text: "Average value times area optional.", why: "Carry the algebra carefully." },
      { text: "Report $8$.", why: "Carry the algebra carefully." },
    ],
    result: "$8$",
    check: "Swapped order also gives $8$.",
    mistake: "Wrong inner limits.",
    life: "Cut-and-fill volumes, rainfall totals over a region, and average temperature on a map are double integrals with the same setups as Part 1.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Type I region",
    setup: "$\\iint_D y\\,dA$ under $y=1-x^2$, $y\\ge 0$.",
    steps: [
      { text: "$x$ from $-1$ to $1$.", why: "Carry the algebra carefully." },
      { text: "For each $x$, $y:0\\to 1-x^2$.", why: "Carry the algebra carefully." },
      { text: "$\\int_{-1}^1\\int_0^{1-x^2} y\\,dy\\,dx$.", why: "Carry the algebra carefully." },
      { text: "Inner $\\tfrac12(1-x^2)^2$.", why: "Carry the algebra carefully." },
      { text: "Even integrand \u2014 double $[0,1]$.", why: "Carry the algebra carefully." },
      { text: "Compute $=8/15$.", why: "Carry the algebra carefully." },
      { text: "Sketch the region first.", why: "Carry the algebra carefully." },
      { text: "Report $8/15$.", why: "Carry the algebra carefully." },
    ],
    result: "$8/15$",
    check: "Symmetry halves the work.",
    mistake: "Using $x$ limits of $y$.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "Switch order",
    setup: "Rewrite $\\int_0^1\\int_x^1 f\\,dy\\,dx$ as $dx\\,dy$.",
    steps: [
      { text: "Region: $0\\le x\\le 1$, $x\\le y\\le 1$.", why: "Carry the algebra carefully." },
      { text: "Equiv: $0\\le y\\le 1$, $0\\le x\\le y$.", why: "Carry the algebra carefully." },
      { text: "$\\int_0^1\\int_0^y f\\,dx\\,dy$.", why: "Carry the algebra carefully." },
      { text: "Sketch triangle.", why: "Carry the algebra carefully." },
      { text: "Vertices $(0,0),(0,1),(1,1)$.", why: "Carry the algebra carefully." },
      { text: "Same set of points.", why: "Carry the algebra carefully." },
      { text: "Useful when antiderivative prefers $x$ first.", why: "Carry the algebra carefully." },
      { text: "State new limits.", why: "Carry the algebra carefully." },
    ],
    result: "$\\int_0^1\\int_0^y f\\,dx\\,dy$",
    check: "Corner check passes.",
    mistake: "Swapping without redrawing.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "Polar area",
    setup: "Area of $r\\le 2$ via $\\iint r\\,dr\\,d\\theta$.",
    steps: [
      { text: "$\\theta:0\\to 2\\pi$, $r:0\\to 2$.", why: "Carry the algebra carefully." },
      { text: "$\\int_0^{2\\pi}\\int_0^2 r\\,dr\\,d\\theta$.", why: "Carry the algebra carefully." },
      { text: "Inner $2$, outer $4\\pi$.", why: "Carry the algebra carefully." },
      { text: "Matches $\\pi R^2$.", why: "Carry the algebra carefully." },
      { text: "Jacobian $r$ is mandatory.", why: "Carry the algebra carefully." },
      { text: "Full disk.", why: "Carry the algebra carefully." },
      { text: "Report $4\\pi$.", why: "Carry the algebra carefully." },
      { text: "Sanity: radius $2$.", why: "Carry the algebra carefully." },
    ],
    result: "$4\\pi$",
    check: "Circle area formula agrees.",
    mistake: "Forgetting Jacobian $r$.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "Polar integrand",
    setup: "$\\iint_D e^{-(x^2+y^2)}\\,dA$ over unit disk.",
    steps: [
      { text: "Polar: $\\int_0^{2\\pi}\\int_0^1 e^{-r^2} r\\,dr\\,d\\theta$.", why: "Carry the algebra carefully." },
      { text: "$u=r^2$, $du=2r\\,dr$.", why: "Carry the algebra carefully." },
      { text: "Inner $(1-e^{-1})/2$.", why: "Carry the algebra carefully." },
      { text: "Times $2\\pi$: $\\pi(1-e^{-1})$.", why: "Carry the algebra carefully." },
      { text: "Gaussian over disk.", why: "Carry the algebra carefully." },
      { text: "Limits simple in polar.", why: "Carry the algebra carefully." },
      { text: "Report exact value.", why: "Carry the algebra carefully." },
      { text: "Check positivity.", why: "Carry the algebra carefully." },
    ],
    result: "$\\pi(1-1/e)$",
    check: "Positive and less than $\\pi$.",
    mistake: "Dropping $r$ in $du$.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Average value",
    setup: "Average of $f=x$ on $[0,2]\\\\times[0,1]$.",
    steps: [
      { text: "Area $=2$.", why: "Carry the algebra carefully." },
      { text: "$\\iint x\\,dA=\\int_0^2\\int_0^1 x\\,dy\\,dx=2$.", why: "Carry the algebra carefully." },
      { text: "Average $=1$.", why: "Carry the algebra carefully." },
      { text: "Makes sense midway in $x$.", why: "Carry the algebra carefully." },
      { text: "Formula $(\\iint f)/\\\\mathrm{Area}$.", why: "Carry the algebra carefully." },
      { text: "Units match $f$.", why: "Carry the algebra carefully." },
      { text: "Report $1$.", why: "Carry the algebra carefully." },
      { text: "Quick mental check.", why: "Carry the algebra carefully." },
    ],
    result: "$1$",
    check: "Midpoint of $x$-range.",
    mistake: "Forgetting to divide by area.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Mass with density",
    setup: "Mass of square $[0,1]^2$ with $\\rho=x+y$.",
    steps: [
      { text: "$m=\\iint\\rho\\,dA$.", why: "Carry the algebra carefully." },
      { text: "$\\int_0^1\\int_0^1(x+y)\\,dy\\,dx$.", why: "Carry the algebra carefully." },
      { text: "Inner $x+\\tfrac12$.", why: "Carry the algebra carefully." },
      { text: "Outer $1$.", why: "Carry the algebra carefully." },
      { text: "Mass $=1$.", why: "Carry the algebra carefully." },
      { text: "Density nonnegative.", why: "Carry the algebra carefully." },
      { text: "Centroid next optional.", why: "Carry the algebra carefully." },
      { text: "Report mass $1$.", why: "Carry the algebra carefully." },
    ],
    result: "$m=1$",
    check: "Symmetric density average $1$.",
    mistake: "Integrating $\\rho$ without $dA$ limits.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Fubini caution",
    setup: "When may you freely switch order?",
    steps: [
      { text: "Need continuity on a closed bounded region, or absolute integrability.", why: "Carry the algebra carefully." },
      { text: "Otherwise counterexamples exist.", why: "Carry the algebra carefully." },
      { text: "Sketch first.", why: "Carry the algebra carefully." },
      { text: "Check corner continuity.", why: "Carry the algebra carefully." },
      { text: "Polar often removes singularities.", why: "Carry the algebra carefully." },
      { text: "State hypothesis.", why: "Carry the algebra carefully." },
      { text: "Then switch.", why: "Carry the algebra carefully." },
      { text: "Cite Fubini/Tonelli.", why: "Carry the algebra carefully." },
    ],
    result: "Need hypotheses for Fubini",
    check: "Continuous on compact set is safe.",
    mistake: "Switching blindly on improper integrals.",
  },
];

export const INTEGRALS_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Triple box",
    setup: "$\\iiint_B xyz\\,dV$, $B=[0,1]^3$.",
    steps: [
      { text: "Separate: $(\\int_0^1 x\\,dx)(\\\\int_0^1 y\\,dy)(\\\\int_0^1 z\\,dz)$.", why: "Carry the algebra carefully." },
      { text: "Each factor $1/2$.", why: "Carry the algebra carefully." },
      { text: "Product $1/8$.", why: "Carry the algebra carefully." },
      { text: "Positive octant unit cube.", why: "Carry the algebra carefully." },
      { text: "Fubini in 3D.", why: "Carry the algebra carefully." },
      { text: "Check by iterating.", why: "Carry the algebra carefully." },
      { text: "Report $1/8$.", why: "Carry the algebra carefully." },
      { text: "Symmetry note.", why: "Carry the algebra carefully." },
    ],
    result: "$1/8$",
    check: "Three identical factors.",
    mistake: "Wrong power on the product.",
    life: "Tank volumes, atmospheric mass columns, and 3D printing material estimates are triple integrals \u2014 cylindrical and spherical just match the symmetry.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Volume under cone",
    setup: "Volume under $z=\\sqrt{x^2+y^2}$ above the unit disk.",
    steps: [
      { text: "In polar, $z=r$ over $r\\le 1$.", why: "Match symmetry." },
      { text: "$V=\\iint_D r\\,dA=\\int_0^{2\\pi}\\int_0^1 r\\cdot r\\,dr\\,d\\theta$.", why: "Height times area element." },
      { text: "Inner $\\int_0^1 r^2\\,dr=1/3$.", why: "Power rule." },
      { text: "Outer factor $2\\pi$ yields $2\\pi/3$.", why: "Full angle." },
      { text: "Include Jacobian $r$ once via $dA=r\\,dr\\,d\\theta$.", why: "Polar area." },
      { text: "Solid is an inverted cone of height 1.", why: "Geometry check." },
      { text: "Compare to $\\tfrac13\\pi R^2 H$ only for upright cone tip-up; here volume is $2\\pi/3$.", why: "Different solid." },
      { text: "Report $V=2\\pi/3$.", why: "Final answer." },
    ],
    result: "$2\\pi/3$",
    check: "Positive and plausible for unit disk.",
    mistake: "Forgetting the Jacobian $r$.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "Spherical mass",
    setup: "Mass of unit ball with $\\\\rho=1$.",
    steps: [
      { text: "$dV=\\\\rho^2\\\\sin\\\\phi\\\\,d\\\\rho\\\\,d\\\\phi\\\\,d\\\\theta$.", why: "Carry the algebra carefully." },
      { text: "$\\\\rho:0\\\\to 1$, $\\\\phi:0\\\\to\\\\pi$, $\\\\theta:0\\\\to 2\\\\pi$.", why: "Carry the algebra carefully." },
      { text: "Integral $=4\\\\pi/3$.", why: "Carry the algebra carefully." },
      { text: "Volume of unit ball.", why: "Carry the algebra carefully." },
      { text: "Density 1 means mass=volume.", why: "Carry the algebra carefully." },
      { text: "Report $4\\\\pi/3$.", why: "Carry the algebra carefully." },
      { text: "Check solid angle $4\\\\pi$.", why: "Carry the algebra carefully." },
      { text: "Standard result.", why: "Carry the algebra carefully." },
    ],
    result: "$4\\\\pi/3$",
    check: "Matches ball volume.",
    mistake: "Wrong $\\\\sin\\\\phi$ power.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "Change order triple",
    setup: "Describe projecting $E: z\\\\ge x^2+y^2$, $z\\\\le 1$ onto $xy$-plane.",
    steps: [
      { text: "Paraboloid under plane $z=1$.", why: "Carry the algebra carefully." },
      { text: "Intersection $x^2+y^2=1$.", why: "Carry the algebra carefully." },
      { text: "Disk $D: x^2+y^2\\\\le 1$.", why: "Carry the algebra carefully." },
      { text: "For $(x,y)$ in $D$, $z$ from $x^2+y^2$ to $1$.", why: "Carry the algebra carefully." },
      { text: "Volume $\\\\iint_D(1-x^2-y^2)\\\\,dA$.", why: "Carry the algebra carefully." },
      { text: "Polar evaluates to $\\\\pi/2$.", why: "Carry the algebra carefully." },
      { text: "Classic solid.", why: "Carry the algebra carefully." },
      { text: "State projection $D$.", why: "Carry the algebra carefully." },
    ],
    result: "$D: x^2+y^2\\\\le 1$, $z: r^2\\\\to 1$",
    check: "Volume $\\\\pi/2$.",
    mistake: "Using exterior of the disk.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "Cylindrical Jacobian",
    setup: "Why does $dV$ gain a factor $r$ in cylindrical?",
    steps: [
      { text: "Map $(r,\\\\theta,z)\\\\to(x,y,z)$.", why: "Carry the algebra carefully." },
      { text: "Jacobian determinant magnitude is $r$.", why: "Carry the algebra carefully." },
      { text: "Same $r$ as polar area.", why: "Carry the algebra carefully." },
      { text: "Vanishes on the axis $r=0$ (measure zero).", why: "Carry the algebra carefully." },
      { text: "Always include $r\\\\,dr\\\\,d\\\\theta\\\\,dz$.", why: "Carry the algebra carefully." },
      { text: "Common exam omission.", why: "Carry the algebra carefully." },
      { text: "State $dV=r\\\\,dr\\\\,d\\\\theta\\\\,dz$.", why: "Carry the algebra carefully." },
      { text: "Tie to area element.", why: "Carry the algebra carefully." },
    ],
    result: "$dV=r\\\\,dr\\\\,d\\\\theta\\\\,dz$",
    check: "Reduces to polar when no $z$.",
    mistake: "Writing $dx\\\\,dy\\\\,dz$ in $r\\\\theta z$ coords.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Center of mass idea",
    setup: "Outline $\\\\bar x$ for lamina with density $\\\\rho$.",
    steps: [
      { text: "$m=\\\\iint\\\\rho\\\\,dA$.", why: "Carry the algebra carefully." },
      { text: "$M_y=\\\\iint x\\\\rho\\\\,dA$.", why: "Carry the algebra carefully." },
      { text: "$\\\\bar x=M_y/m$.", why: "Carry the algebra carefully." },
      { text: "Analogous $\\\\bar y$.", why: "Carry the algebra carefully." },
      { text: "Triple for solids with $z$.", why: "Carry the algebra carefully." },
      { text: "Symmetry often forces $\\\\bar x=0$.", why: "Carry the algebra carefully." },
      { text: "State formulas.", why: "Carry the algebra carefully." },
      { text: "Compute only if data given.", why: "Carry the algebra carefully." },
    ],
    result: "$\\\\bar x=M_y/m$",
    check: "Balances first moments.",
    mistake: "Using second moments by mistake.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Spherical integrand",
    setup: "$\\\\iiint_E (x^2+y^2+z^2)\\\\,dV$ over unit ball.",
    steps: [
      { text: "In spherical: $\\\\iiint \\\\rho^2\\\\cdot\\\\rho^2\\\\sin\\\\phi\\\\,d\\\\rho\\\\,d\\\\phi\\\\,d\\\\theta$.", why: "Carry the algebra carefully." },
      { text: "$\\\\int_0^{2\\\\pi}\\\\int_0^{\\\\pi}\\\\int_0^1 \\\\rho^4\\\\sin\\\\phi\\\\,d\\\\rho\\\\,d\\\\phi\\\\,d\\\\theta$.", why: "Carry the algebra carefully." },
      { text: "Inner $1/5$.", why: "Carry the algebra carefully." },
      { text: "$\\\\phi$ gives $2$.", why: "Carry the algebra carefully." },
      { text: "$\\\\theta$ gives $2\\\\pi$.", why: "Carry the algebra carefully." },
      { text: "Total $4\\\\pi/5$.", why: "Carry the algebra carefully." },
      { text: "Report $4\\\\pi/5$.", why: "Carry the algebra carefully." },
      { text: "Radial weight $\\\\rho^2$.", why: "Carry the algebra carefully." },
    ],
    result: "$4\\\\pi/5$",
    check: "Positive less than volume.",
    mistake: "Using $\\\\rho^2$ only once.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Application volume",
    setup: "Set up volume of ice cream: hemisphere $z=\\\\sqrt{1-x^2-y^2}$ above cone $z=\\\\sqrt{x^2+y^2}$.",
    steps: [
      { text: "Intersection: $r=\\\\sqrt{1-r^2}\\\\Rightarrow r=1/\\\\sqrt{2}$.", why: "Carry the algebra carefully." },
      { text: "Prefer spherical or cylindrical.", why: "Carry the algebra carefully." },
      { text: "Cylindrical: for $r\\\\le 1/\\\\sqrt{2}$, $z$ from $r$ to $\\\\sqrt{1-r^2}$.", why: "Carry the algebra carefully." },
      { text: "Integrate $d\\\\theta\\\\, r\\\\,dr$.", why: "Carry the algebra carefully." },
      { text: "Classic solid.", why: "Carry the algebra carefully." },
      { text: "Sketch first.", why: "Carry the algebra carefully." },
      { text: "Leave setup if asked.", why: "Carry the algebra carefully." },
      { text: "Compute if required.", why: "Carry the algebra carefully." },
    ],
    result: "Cylindrical setup over $r\\\\le 1/\\\\sqrt{2}$",
    check: "Intersection circle correct.",
    mistake: "Integrating to $r=1$.",
  },
];

export const LAGRANGE_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Setup system",
    setup: "Optimize $f=x^2+y^2$ subject to $g=x+y-1=0$.",
    steps: [
      { text: "$\\\\nabla f=\\\\langle 2x,2y\\\\rangle$, $\\\\nabla g=\\\\langle 1,1\\\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$2x=\\\\lambda$, $2y=\\\\lambda$.", why: "Carry the algebra carefully." },
      { text: "So $x=y$.", why: "Carry the algebra carefully." },
      { text: "Constraint $2x=1$.", why: "Carry the algebra carefully." },
      { text: "$(1/2,1/2)$, $\\\\lambda=1$.", why: "Carry the algebra carefully." },
      { text: "$f=1/2$.", why: "Carry the algebra carefully." },
      { text: "Only constrained critical point.", why: "Carry the algebra carefully." },
      { text: "Geometry: closest point to origin on the line.", why: "Carry the algebra carefully." },
    ],
    result: "$(1/2,1/2)$, $f=1/2$",
    check: "Substitution method agrees.",
    mistake: "Forgetting the constraint equation.",
    life: "Budgeted production, beam strength under weight limits, and portfolio weights that sum to 1 are Lagrange problems: optimize with a hard constraint.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Meaning of $\\lambda$",
    setup: "Interpret $\\\\lambda$ for a budget constraint.",
    steps: [
      { text: "$\\\\nabla f=\\\\lambda\\\\nabla g$ means level sets share a tangent.", why: "Carry the algebra carefully." },
      { text: "$\\\\lambda$ is the marginal gain in $f$ per relaxation of $g$.", why: "Carry the algebra carefully." },
      { text: "Economics: shadow price.", why: "Carry the algebra carefully." },
      { text: "Engineering: sensitivity to resource.", why: "Carry the algebra carefully." },
      { text: "Sign depends on constraint form.", why: "Carry the algebra carefully." },
      { text: "Not always needed numerically.", why: "Carry the algebra carefully." },
      { text: "Still report when asked.", why: "Carry the algebra carefully." },
      { text: "State the geometric picture.", why: "Carry the algebra carefully." },
    ],
    result: "Shadow price / shared tangent",
    check: "Matches envelope theorem intuition.",
    mistake: "Treating $\\\\lambda$ as a coordinate.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "Circle constraint",
    setup: "Max $f=x$ on $x^2+y^2=1$.",
    steps: [
      { text: "$\\\\nabla f=\\\\langle 1,0\\\\rangle$, $\\\\nabla g=\\\\langle 2x,2y\\\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$1=\\\\lambda 2x$, $0=\\\\lambda 2y$.", why: "Carry the algebra carefully." },
      { text: "From second, $y=0$ or $\\\\lambda=0$ (impossible with first).", why: "Carry the algebra carefully." },
      { text: "$y=0$, $x=\\\\pm 1$.", why: "Carry the algebra carefully." },
      { text: "First: $\\\\lambda=1/(2x)$.", why: "Carry the algebra carefully." },
      { text: "Max at $(1,0)$, min at $(-1,0)$.", why: "Carry the algebra carefully." },
      { text: "$f=\\\\pm 1$.", why: "Carry the algebra carefully." },
      { text: "Obvious geometrically.", why: "Carry the algebra carefully." },
    ],
    result: "Max $1$ at $(1,0)$",
    check: "Unit circle extreme $x$.",
    mistake: "Missing the negative point.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "Eliminate $\\lambda$",
    setup: "From $f_x=\\\\lambda g_x$, $f_y=\\\\lambda g_y$, eliminate $\\\\lambda$.",
    steps: [
      { text: "If $g_x\\\\neq 0$, $\\\\lambda=f_x/g_x$.", why: "Carry the algebra carefully." },
      { text: "Also $\\\\lambda=f_y/g_y$ when $g_y\\\\neq 0$.", why: "Carry the algebra carefully." },
      { text: "Cross-multiply $f_x g_y=f_y g_x$.", why: "Carry the algebra carefully." },
      { text: "Plus constraint $g=0$.", why: "Carry the algebra carefully." },
      { text: "Two equations in $(x,y)$.", why: "Carry the algebra carefully." },
      { text: "Handle vanishing gradient components separately.", why: "Carry the algebra carefully." },
      { text: "Standard algebra trick.", why: "Carry the algebra carefully." },
      { text: "State the cross rule.", why: "Carry the algebra carefully." },
    ],
    result: "$f_x g_y=f_y g_x$ (+ $g=0$)",
    check: "Equivalent to parallel gradients.",
    mistake: "Dividing by zero casually.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "Compact constraint",
    setup: "Why must extrema exist on $x^2+y^2=1$?",
    steps: [
      { text: "Constraint set is closed and bounded.", why: "Carry the algebra carefully." },
      { text: "Continuous $f$ attains max/min.", why: "Carry the algebra carefully." },
      { text: "Lagrange finds candidates.", why: "Carry the algebra carefully." },
      { text: "Compare $f$ values.", why: "Carry the algebra carefully." },
      { text: "No boundary of the constraint beyond itself.", why: "Carry the algebra carefully." },
      { text: "EVT applies.", why: "Carry the algebra carefully." },
      { text: "State existence first.", why: "Carry the algebra carefully." },
      { text: "Then solve.", why: "Carry the algebra carefully." },
    ],
    result: "EVT on compact constraint",
    check: "Circle is compact.",
    mistake: "Assuming a max without justification.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Two variables one constraint",
    setup: "Count degrees of freedom.",
    steps: [
      { text: "2 variables, 1 constraint $\\\\Rightarrow$ 1 free parameter.", why: "Carry the algebra carefully." },
      { text: "Lagrange adds $\\\\lambda$.", why: "Carry the algebra carefully." },
      { text: "3 equations in 3 unknowns.", why: "Carry the algebra carefully." },
      { text: "Square system (generically).", why: "Carry the algebra carefully." },
      { text: "May have multiple solutions.", why: "Carry the algebra carefully." },
      { text: "Compare objective values.", why: "Carry the algebra carefully." },
      { text: "Sketch helps.", why: "Carry the algebra carefully." },
      { text: "State the count.", why: "Carry the algebra carefully." },
    ],
    result: "3 eqs in $(x,y,\\\\lambda)$",
    check: "Matches substitution DOF.",
    mistake: "Forgetting to include $g=0$.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Gradient parallel",
    setup: "Why $\\\\nabla f\\\\parallel\\\\nabla g$ at constrained extrema?",
    steps: [
      { text: "Move on constraint: velocity $\\\\mathbf{v}$ with $\\\\nabla g\\\\cdot\\\\mathbf{v}=0$.", why: "Carry the algebra carefully." },
      { text: "For $f$ stationary, $\\\\nabla f\\\\cdot\\\\mathbf{v}=0$ for all such $\\\\mathbf{v}$.", why: "Carry the algebra carefully." },
      { text: "So $\\\\nabla f$ is normal to the tangent space.", why: "Carry the algebra carefully." },
      { text: "Same as $\\\\nabla g$.", why: "Carry the algebra carefully." },
      { text: "Hence collinear.", why: "Carry the algebra carefully." },
      { text: "$\\\\nabla f=\\\\lambda\\\\nabla g$.", why: "Carry the algebra carefully." },
      { text: "Geometric core of the method.", why: "Carry the algebra carefully." },
      { text: "State parallelism.", why: "Carry the algebra carefully." },
    ],
    result: "$\\\\nabla f=\\\\lambda\\\\nabla g$",
    check: "Tangency of level sets.",
    mistake: "Setting $\\\\nabla f=\\\\mathbf{0}$ only.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Worked numbers",
    setup: "Optimize $f=xy$ on $x+2y=4$.",
    steps: [
      { text: "$\\\\nabla f=\\\\langle y,x\\\\rangle$, $\\\\nabla g=\\\\langle 1,2\\\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$y=\\\\lambda$, $x=2\\\\lambda$.", why: "Carry the algebra carefully." },
      { text: "$x=2y$.", why: "Carry the algebra carefully." },
      { text: "$2y+2y=4\\\\Rightarrow y=1$, $x=2$.", why: "Carry the algebra carefully." },
      { text: "$f=2$.", why: "Carry the algebra carefully." },
      { text: "$\\\\lambda=1$.", why: "Carry the algebra carefully." },
      { text: "Substitution $x=4-2y$ agrees.", why: "Carry the algebra carefully." },
      { text: "Report point and value.", why: "Carry the algebra carefully." },
    ],
    result: "$(2,1)$, $f=2$",
    check: "AM-GM style check.",
    mistake: "Arithmetic slip on constraint.",
  },
];

export const LAGRANGE_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Two constraints",
    setup: "Outline optimizing $f$ with $g=0$ and $h=0$.",
    steps: [
      { text: "$\\\\nabla f=\\\\lambda\\\\nabla g+\\\\mu\\\\nabla h$.", why: "Carry the algebra carefully." },
      { text: "Plus $g=h=0$.", why: "Carry the algebra carefully." },
      { text: "$n$ vars + 2 multipliers.", why: "Carry the algebra carefully." },
      { text: "$n+2$ equations.", why: "Carry the algebra carefully." },
      { text: "Intersection of two surfaces is a curve.", why: "Carry the algebra carefully." },
      { text: "Tangency in the normal plane.", why: "Carry the algebra carefully." },
      { text: "Solve systematically.", why: "Carry the algebra carefully." },
      { text: "Compare candidate values.", why: "Carry the algebra carefully." },
    ],
    result: "$\\\\nabla f=\\\\lambda\\\\nabla g+\\\\mu\\\\nabla h$",
    check: "Normal cone picture.",
    mistake: "Using only one multiplier.",
    life: "Multi-constraint design \u2014 truss members with several load and length limits \u2014 uses the same normal-cone idea as Part 2 Lagrange.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Inequality preview",
    setup: "How do KKT conditions extend equality Lagrange?",
    steps: [
      { text: "Active inequalities behave like equalities.", why: "Carry the algebra carefully." },
      { text: "Inactive ones have multiplier $0$.", why: "Carry the algebra carefully." },
      { text: "Complementary slackness.", why: "Carry the algebra carefully." },
      { text: "Still $\\\\nabla f$ in cone of active gradients.", why: "Carry the algebra carefully." },
      { text: "Beyond basic HSSC but good foresight.", why: "Carry the algebra carefully." },
      { text: "Equality case is Lagrange.", why: "Carry the algebra carefully." },
      { text: "State the idea.", why: "Carry the algebra carefully." },
      { text: "Don't overclaim on exams that only ask equality.", why: "Carry the algebra carefully." },
    ],
    result: "Active-set / KKT idea",
    check: "Reduces to Lagrange when all equalities.",
    mistake: "Ignoring activity.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "Multiple candidates",
    setup: "$f=x^2+y^2$ on $x^4+y^4=1$ \u2014 strategy.",
    steps: [
      { text: "Write Lagrange system.", why: "Carry the algebra carefully." },
      { text: "Use symmetry $x=\\\\pm y$ and axis hits.", why: "Carry the algebra carefully." },
      { text: "Several points.", why: "Carry the algebra carefully." },
      { text: "Evaluate $f$ at each.", why: "Carry the algebra carefully." },
      { text: "Max/min among them.", why: "Carry the algebra carefully." },
      { text: "Numerics OK if exact messy.", why: "Carry the algebra carefully." },
      { text: "Sketch the squircle.", why: "Carry the algebra carefully." },
      { text: "Report comparison table.", why: "Carry the algebra carefully." },
    ],
    result: "List all candidates, compare $f$",
    check: "Symmetry cuts work.",
    mistake: "Stopping at first solution.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "Dependent constraints",
    setup: "What if $\\\\nabla g$ and $\\\\nabla h$ are parallel?",
    steps: [
      { text: "Constraint qualification fails.", why: "Carry the algebra carefully." },
      { text: "Normals not independent.", why: "Carry the algebra carefully." },
      { text: "Method may miss or mislead.", why: "Carry the algebra carefully." },
      { text: "Check rank of Jacobian of constraints.", why: "Carry the algebra carefully." },
      { text: "Reformulate constraints.", why: "Carry the algebra carefully." },
      { text: "Rare but exam-aware.", why: "Carry the algebra carefully." },
      { text: "State CQ.", why: "Carry the algebra carefully." },
      { text: "Proceed only if independent.", why: "Carry the algebra carefully." },
    ],
    result: "Need independent $\\\\nabla g,\\\\nabla h$",
    check: "Jacobian rank check.",
    mistake: "Blindly solving singular systems.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "Economics shadow prices",
    setup: "Two resources, interpret $(\\\\lambda,\\\\mu)$.",
    steps: [
      { text: "Each multiplier is marginal value of relaxing that resource.", why: "Carry the algebra carefully." },
      { text: "Compare which constraint binds value.", why: "Carry the algebra carefully." },
      { text: "Sign conventions depend on $g\\\\le 0$ vs $=0$.", why: "Carry the algebra carefully." },
      { text: "Report with units if given.", why: "Carry the algebra carefully." },
      { text: "Sensitivity analysis.", why: "Carry the algebra carefully." },
      { text: "Useful in OR.", why: "Carry the algebra carefully." },
      { text: "State interpretation.", why: "Carry the algebra carefully." },
      { text: "Compute from system.", why: "Carry the algebra carefully." },
    ],
    result: "Marginal values of resources",
    check: "Envelope theorem link.",
    mistake: "Treating multipliers as decision vars to optimize freely.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Sphere + plane",
    setup: "Max $f=z$ on $x^2+y^2+z^2=1$ and $x+y+z=1$.",
    steps: [
      { text: "Intersection is a circle.", why: "Carry the algebra carefully." },
      { text: "Lagrange with two multipliers.", why: "Carry the algebra carefully." },
      { text: "Or parameterize the circle.", why: "Carry the algebra carefully." },
      { text: "Highest $z$ on that circle.", why: "Carry the algebra carefully." },
      { text: "Solve linear algebra for normals.", why: "Carry the algebra carefully." },
      { text: "Get explicit point.", why: "Carry the algebra carefully." },
      { text: "Verify on both constraints.", why: "Carry the algebra carefully." },
      { text: "Report max $z$.", why: "Carry the algebra carefully." },
    ],
    result: "Highest point on the circle",
    check: "Both constraints satisfied.",
    mistake: "Using only the sphere.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Substitution vs Lagrange",
    setup: "When is substitution easier?",
    steps: [
      { text: "When constraint solves cleanly for a variable.", why: "Carry the algebra carefully." },
      { text: "Linear constraints shine.", why: "Carry the algebra carefully." },
      { text: "Lagrange better for circles/spheres.", why: "Carry the algebra carefully." },
      { text: "Same critical points if done right.", why: "Carry the algebra carefully." },
      { text: "Use whichever is shorter.", why: "Carry the algebra carefully." },
      { text: "Check answers two ways when unsure.", why: "Carry the algebra carefully." },
      { text: "State preference rule.", why: "Carry the algebra carefully." },
      { text: "Show one worked path.", why: "Carry the algebra carefully." },
    ],
    result: "Solve constraint if easy; else Lagrange",
    check: "Cross-check on a simple problem.",
    mistake: "Forcing Lagrange on $y=1-x$.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Exam checklist",
    setup: "List a certificate checklist for Lagrange solutions.",
    steps: [
      { text: "Write $f$, $g$ clearly.", why: "Carry the algebra carefully." },
      { text: "Compute gradients.", why: "Carry the algebra carefully." },
      { text: "Write full system including $g=0$.", why: "Carry the algebra carefully." },
      { text: "Solve all branches.", why: "Carry the algebra carefully." },
      { text: "Evaluate $f$ at each candidate.", why: "Carry the algebra carefully." },
      { text: "Select max/min as asked.", why: "Carry the algebra carefully." },
      { text: "Interpret $\\\\lambda$ if requested.", why: "Carry the algebra carefully." },
      { text: "Box the final point and value.", why: "Carry the algebra carefully." },
    ],
    result: "System \u2192 candidates \u2192 compare $f$",
    check: "Matches board marking schemes.",
    mistake: "Omitting $g=0$ from the system.",
  },
];

export const DIVCURL_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Divergence compute",
    setup: "$\\\\mathbf{F}=\\\\langle x^2,y^2,z^2\\\\rangle$. Find $\\\\nabla\\\\cdot\\\\mathbf{F}$.",
    steps: [
      { text: "$\\\\partial/\\\\partial x(x^2)=2x$.", why: "Carry the algebra carefully." },
      { text: "$\\\\partial/\\\\partial y(y^2)=2y$.", why: "Carry the algebra carefully." },
      { text: "$\\\\partial/\\\\partial z(z^2)=2z$.", why: "Carry the algebra carefully." },
      { text: "$\\\\mathrm{div}\\\\,\\\\mathbf{F}=2x+2y+2z$.", why: "Carry the algebra carefully." },
      { text: "Source strength at a point.", why: "Carry the algebra carefully." },
      { text: "Positive means net outflow locally.", why: "Carry the algebra carefully." },
      { text: "At origin divergence $0$.", why: "Carry the algebra carefully." },
      { text: "Report $2(x+y+z)$.", why: "Carry the algebra carefully." },
    ],
    result: "$2(x+y+z)$",
    check: "Componentwise sum.",
    mistake: "Taking curl by mistake.",
    life: "Storm forecasts and pipe-network models read divergence as local source/sink strength and curl as swirl \u2014 exactly Part 1's operators.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Curl compute",
    setup: "$\\\\mathbf{F}=\\\\langle -y,x,0\\\\rangle$. Find $\\\\nabla\\\\times\\\\mathbf{F}$.",
    steps: [
      { text: "i component: $\\\\partial P/\\\\partial y-\\\\partial N/\\\\partial z=0$.", why: "Carry the algebra carefully." },
      { text: "Wait $P=0$, $N=x$, $M=-y$.", why: "Carry the algebra carefully." },
      { text: "$\\\\nabla\\\\times\\\\mathbf{F}=\\\\langle 0,0,\\\\partial N/\\\\partial x-\\\\partial M/\\\\partial y\\\\rangle$.", why: "Carry the algebra carefully." },
      { text: "$\\\\partial N/\\\\partial x=1$, $\\\\partial M/\\\\partial y=-1$.", why: "Carry the algebra carefully." },
      { text: "Curl $=\\\\langle 0,0,2\\\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Rotation about $z$-axis.", why: "Carry the algebra carefully." },
      { text: "Matches whirlpool.", why: "Carry the algebra carefully." },
      { text: "Report $\\\\langle 0,0,2\\\\rangle$.", why: "Carry the algebra carefully." },
    ],
    result: "$\\\\langle 0,0,2\\\\rangle$",
    check: "2D curl $N_x-M_y=2$.",
    mistake: "Sign error in determinant.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "div curl = 0",
    setup: "Show $\\\\nabla\\\\cdot(\\\\nabla\\\\times\\\\mathbf{F})=0$ for smooth $\\\\mathbf{F}$.",
    steps: [
      { text: "Expand in components.", why: "Carry the algebra carefully." },
      { text: "Mixed partials cancel in pairs.", why: "Carry the algebra carefully." },
      { text: "Identity holds pointwise.", why: "Carry the algebra carefully." },
      { text: "Curl fields are incompressible.", why: "Carry the algebra carefully." },
      { text: "Need $C^2$ smoothness.", why: "Carry the algebra carefully." },
      { text: "Used in Maxwell checks.", why: "Carry the algebra carefully." },
      { text: "State the identity.", why: "Carry the algebra carefully." },
      { text: "Quick symbolic verify on an example.", why: "Carry the algebra carefully." },
    ],
    result: "$\\\\mathrm{div}\\\\,\\\\mathrm{curl}=0$",
    check: "Example $\\\\mathbf{F}=\\\\langle -y,x,0\\\\rangle$ has div curl $0$.",
    mistake: "Claiming it without smoothness.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "curl grad = 0",
    setup: "Show $\\\\nabla\\\\times(\\\\nabla f)=\\\\mathbf{0}$.",
    steps: [
      { text: "Gradient $\\\\langle f_x,f_y,f_z\\\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Curl components are mixed partial differences.", why: "Carry the algebra carefully." },
      { text: "Clairaut $\\\\Rightarrow$ zeros.", why: "Carry the algebra carefully." },
      { text: "Conservative fields are irrotational.", why: "Carry the algebra carefully." },
      { text: "Converse needs topology.", why: "Carry the algebra carefully." },
      { text: "State identity.", why: "Carry the algebra carefully." },
      { text: "Tie to Part 1 vector calc.", why: "Carry the algebra carefully." },
      { text: "Example $f=x^2 y$.", why: "Carry the algebra carefully." },
    ],
    result: "$\\\\mathrm{curl}\\\\,\\\\mathrm{grad}=\\\\mathbf{0}$",
    check: "Matches conservative test.",
    mistake: "Forgetting domain issues for converse.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "Physical div",
    setup: "Interpret $\\\\nabla\\\\cdot\\\\mathbf{v}$ for fluid velocity.",
    steps: [
      { text: "Positive: local expansion / source.", why: "Carry the algebra carefully." },
      { text: "Negative: compression / sink.", why: "Carry the algebra carefully." },
      { text: "Zero: incompressible.", why: "Carry the algebra carefully." },
      { text: "Units: rate of volume strain.", why: "Carry the algebra carefully." },
      { text: "Weather models track this.", why: "Carry the algebra carefully." },
      { text: "Pointwise quantity.", why: "Carry the algebra carefully." },
      { text: "Integral form via divergence theorem.", why: "Carry the algebra carefully." },
      { text: "State the reading.", why: "Carry the algebra carefully." },
    ],
    result: "Local expansion rate",
    check: "Matches continuity equation stories.",
    mistake: "Confusing with curl.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Physical curl",
    setup: "Interpret $\\\\nabla\\\\times\\\\mathbf{v}$.",
    steps: [
      { text: "Measures local rotation.", why: "Carry the algebra carefully." },
      { text: "Direction: axis of spin (right-hand rule).", why: "Carry the algebra carefully." },
      { text: "Magnitude: twice angular speed for rigid rotation.", why: "Carry the algebra carefully." },
      { text: "Irrotational means curl zero.", why: "Carry the algebra carefully." },
      { text: "Pinwheel test.", why: "Carry the algebra carefully." },
      { text: "Important in aerodynamics.", why: "Carry the algebra carefully." },
      { text: "State vector nature.", why: "Carry the algebra carefully." },
      { text: "Give a whirlpool example.", why: "Carry the algebra carefully." },
    ],
    result: "Local rotation vector",
    check: "Whirlpool $\\\\langle -y,x,0\\\\rangle$ has upward curl.",
    mistake: "Treating curl as a scalar in 3D.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Product rules sketch",
    setup: "State $\\\\nabla\\\\cdot(f\\\\mathbf{F})$ expansion.",
    steps: [
      { text: "$\\\\nabla\\\\cdot(f\\\\mathbf{F})=\\\\nabla f\\\\cdot\\\\mathbf{F}+f\\\\nabla\\\\cdot\\\\mathbf{F}$.", why: "Carry the algebra carefully." },
      { text: "Like product rule.", why: "Carry the algebra carefully." },
      { text: "Useful in identities.", why: "Carry the algebra carefully." },
      { text: "Verify on $f=x$, $\\\\mathbf{F}=\\\\langle x,0,0\\\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Left: $\\\\partial_x(x^2)=2x$.", why: "Carry the algebra carefully." },
      { text: "Right: $1\\\\cdot x+x\\\\cdot 1=2x$.", why: "Carry the algebra carefully." },
      { text: "Match.", why: "Carry the algebra carefully." },
      { text: "Memorize analogous curl rules as needed.", why: "Carry the algebra carefully." },
    ],
    result: "$\\\\nabla f\\\\cdot\\\\mathbf{F}+f\\\\,\\\\mathrm{div}\\\\,\\\\mathbf{F}$",
    check: "Example checks.",
    mistake: "Dropping one term.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Field classification",
    setup: "Classify $\\\\mathbf{F}=\\\\langle x,y,z\\\\rangle$: div and curl.",
    steps: [
      { text: "$\\\\mathrm{div}=3$.", why: "Carry the algebra carefully." },
      { text: "$\\\\mathrm{curl}=\\\\mathbf{0}$.", why: "Carry the algebra carefully." },
      { text: "Source everywhere, no spin.", why: "Carry the algebra carefully." },
      { text: "Not divergence-free.", why: "Carry the algebra carefully." },
      { text: "Irrotational.", why: "Carry the algebra carefully." },
      { text: "Radial explosion field.", why: "Carry the algebra carefully." },
      { text: "Flux through unit sphere $4\\\\pi$.", why: "Carry the algebra carefully." },
      { text: "Report both operators.", why: "Carry the algebra carefully." },
    ],
    result: "div $3$, curl $\\\\mathbf{0}$",
    check: "Divergence theorem foreshadow.",
    mistake: "Computing curl nonzero wrongly.",
  },
];

export const DIVCURL_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Divergence theorem",
    setup: "Flux of $\\\\mathbf{F}=\\\\langle x,y,z\\\\rangle$ through unit sphere.",
    steps: [
      { text: "$\\\\mathrm{div}\\\\,\\\\mathbf{F}=3$.", why: "Carry the algebra carefully." },
      { text: "$\\\\iint_S\\\\mathbf{F}\\\\cdot d\\\\mathbf{S}=\\\\iiint_E 3\\\\,dV$.", why: "Carry the algebra carefully." },
      { text: "Volume $4\\\\pi/3$.", why: "Carry the algebra carefully." },
      { text: "Flux $=4\\\\pi$.", why: "Carry the algebra carefully." },
      { text: "Outward orientation.", why: "Carry the algebra carefully." },
      { text: "No parameterization of sphere.", why: "Carry the algebra carefully." },
      { text: "Classic check.", why: "Carry the algebra carefully." },
      { text: "Report $4\\\\pi$.", why: "Carry the algebra carefully." },
    ],
    result: "$4\\\\pi$",
    check: "Direct radial flux agrees.",
    mistake: "Using surface area $4\\\\pi$ without the field.",
    life: "Engineering flux balances through closed equipment surfaces are divergence-theorem problems \u2014 compute $\\iiint\\mathrm{div}\\,\\mathbf{F}$ instead of six faces.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Incompressible",
    setup: "If $\\\\mathrm{div}\\\\,\\\\mathbf{F}=0$ on $E$, what about closed flux?",
    steps: [
      { text: "Divergence theorem $\\\\Rightarrow$ total outward flux $0$.", why: "Carry the algebra carefully." },
      { text: "What enters equals what leaves.", why: "Carry the algebra carefully." },
      { text: "True for any solid $E$ in the domain.", why: "Carry the algebra carefully." },
      { text: "Used for conservation laws.", why: "Carry the algebra carefully." },
      { text: "State the conclusion.", why: "Carry the algebra carefully." },
      { text: "Need suitable smoothness.", why: "Carry the algebra carefully." },
      { text: "Orientation outward.", why: "Carry the algebra carefully." },
      { text: "Tie to continuity equation.", why: "Carry the algebra carefully." },
    ],
    result: "Net flux $0$",
    check: "Balance of flow.",
    mistake: "Claiming pointwise $\\\\mathbf{F}=\\\\mathbf{0}$.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "Curl theorem preview",
    setup: "Relate Stokes and curl.",
    steps: [
      { text: "Stokes: $\\\\oint_C\\\\mathbf{F}\\\\cdot d\\\\mathbf{r}=\\\\iint_S(\\\\nabla\\\\times\\\\mathbf{F})\\\\cdot d\\\\mathbf{S}$.", why: "Carry the algebra carefully." },
      { text: "Circulation = total curl flux through a spanning surface.", why: "Carry the algebra carefully." },
      { text: "Green is the planar case.", why: "Carry the algebra carefully." },
      { text: "Orientation consistency.", why: "Carry the algebra carefully." },
      { text: "Next guide details.", why: "Carry the algebra carefully." },
      { text: "State the link.", why: "Carry the algebra carefully." },
      { text: "div curl $=0$ implies closed surface flux of curl vanishes.", why: "Carry the algebra carefully." },
      { text: "Consistency check.", why: "Carry the algebra carefully." },
    ],
    result: "Stokes links circulation to curl flux",
    check: "Green special case.",
    mistake: "Using div instead of curl.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "Identity check",
    setup: "Verify $\\\\nabla\\\\times(\\\\nabla\\\\times\\\\mathbf{F})=\\\\nabla(\\\\nabla\\\\cdot\\\\mathbf{F})-\\\\nabla^2\\\\mathbf{F}$ outline.",
    steps: [
      { text: "Standard vector calculus identity.", why: "Carry the algebra carefully." },
      { text: "Component expansion.", why: "Carry the algebra carefully." },
      { text: "Laplacian on each component (Cartesian).", why: "Carry the algebra carefully." },
      { text: "Used in electromagnetism.", why: "Carry the algebra carefully." },
      { text: "Don't memorize every form for HSSC \u2014 recognize name.", why: "Carry the algebra carefully." },
      { text: "State where it appears.", why: "Carry the algebra carefully." },
      { text: "Check on a linear field.", why: "Carry the algebra carefully." },
      { text: "Keep Cartesian caution.", why: "Carry the algebra carefully." },
    ],
    result: "curl curl identity",
    check: "Holds in Cartesian coords.",
    mistake: "Misapplying curvilinear blindly.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "Closed surface of curl",
    setup: "$\\\\iint_S(\\\\nabla\\\\times\\\\mathbf{F})\\\\cdot d\\\\mathbf{S}$ for closed $S$.",
    steps: [
      { text: "Let $\\\\mathbf{G}=\\\\nabla\\\\times\\\\mathbf{F}$.", why: "Carry the algebra carefully." },
      { text: "$\\\\mathrm{div}\\\\,\\\\mathbf{G}=0$.", why: "Carry the algebra carefully." },
      { text: "Divergence theorem $\\\\Rightarrow$ flux $0$.", why: "Carry the algebra carefully." },
      { text: "Independent of $\\\\mathbf{F}$ (smooth).", why: "Carry the algebra carefully." },
      { text: "Consistency of Stokes on closed surfaces.", why: "Carry the algebra carefully." },
      { text: "State zero.", why: "Carry the algebra carefully." },
      { text: "Need oriented closed surface.", why: "Carry the algebra carefully." },
      { text: "Report $0$.", why: "Carry the algebra carefully." },
    ],
    result: "$0$",
    check: "div curl $=0$ + div theorem.",
    mistake: "Computing a nonzero by parameterization error.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Choosing coordinates",
    setup: "When prefer cylindrical for div theorem problems?",
    steps: [
      { text: "When region and field respect axis symmetry.", why: "Carry the algebra carefully." },
      { text: "Sphere $\\\\rightarrow$ spherical.", why: "Carry the algebra carefully." },
      { text: "Box $\\\\rightarrow$ Cartesian.", why: "Carry the algebra carefully." },
      { text: "Setup beats grind.", why: "Carry the algebra carefully." },
      { text: "State the heuristic.", why: "Carry the algebra carefully." },
      { text: "Always compute div first.", why: "Carry the algebra carefully." },
      { text: "If div constant, volume multiplies.", why: "Carry the algebra carefully." },
      { text: "Report strategy.", why: "Carry the algebra carefully." },
    ],
    result: "Match symmetry of $E$ and $\\\\mathbf{F}$",
    check: "Saves algebra.",
    mistake: "Forcing Cartesian on a ball.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Flux vs circulation",
    setup: "Contrast the two measurements.",
    steps: [
      { text: "Flux: through a surface (normal).", why: "Carry the algebra carefully." },
      { text: "Circulation: along a curve (tangent).", why: "Carry the algebra carefully." },
      { text: "Div theorem for flux.", why: "Carry the algebra carefully." },
      { text: "Stokes for circulation.", why: "Carry the algebra carefully." },
      { text: "Different operators.", why: "Carry the algebra carefully." },
      { text: "Exam: read the question verb.", why: "Carry the algebra carefully." },
      { text: "State both.", why: "Carry the algebra carefully." },
      { text: "Give one example each.", why: "Carry the algebra carefully." },
    ],
    result: "Flux\u2194div, circulation\u2194curl",
    check: "Operator matches measurement.",
    mistake: "Answering with the wrong theorem.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Worked box flux",
    setup: "$\\\\mathbf{F}=\\\\langle xy,yz,xz\\\\rangle$ outward on unit cube $[0,1]^3$.",
    steps: [
      { text: "$\\\\mathrm{div}=y+z+x$.", why: "Carry the algebra carefully." },
      { text: "$\\\\iiint(x+y+z)\\\\,dV$.", why: "Carry the algebra carefully." },
      { text: "Each coordinate averages to $1/2$.", why: "Carry the algebra carefully." },
      { text: "Integral $=3/2$.", why: "Carry the algebra carefully." },
      { text: "Could sum six faces \u2014 harder.", why: "Carry the algebra carefully." },
      { text: "Div theorem wins.", why: "Carry the algebra carefully." },
      { text: "Outward on closed cube.", why: "Carry the algebra carefully." },
      { text: "Report $3/2$.", why: "Carry the algebra carefully." },
    ],
    result: "$3/2$",
    check: "Face-by-face would match.",
    mistake: "Wrong div computation.",
  },
];

export const STOKES_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Stokes statement",
    setup: "Write Stokes' Theorem and name each piece.",
    steps: [
      { text: "$\\\\oint_C\\\\mathbf{F}\\\\cdot d\\\\mathbf{r}=\\\\iint_S(\\\\nabla\\\\times\\\\mathbf{F})\\\\cdot d\\\\mathbf{S}$.", why: "Carry the algebra carefully." },
      { text: "Left: circulation on oriented boundary.", why: "Carry the algebra carefully." },
      { text: "Right: curl flux through spanning surface.", why: "Carry the algebra carefully." },
      { text: "Orientation: right-hand rule.", why: "Carry the algebra carefully." },
      { text: "Surface can be deformed if curl allows.", why: "Carry the algebra carefully." },
      { text: "Green is $z=0$ case.", why: "Carry the algebra carefully." },
      { text: "State hypotheses (smoothness).", why: "Carry the algebra carefully." },
      { text: "Box the formula.", why: "Carry the algebra carefully." },
    ],
    result: "Circulation = curl flux",
    check: "Matches Green when planar.",
    mistake: "Writing div on the right.",
    life: "Amp\u00e8re-type loop measurements of magnetic circulation are Stokes problems: a hard loop integral becomes an easier surface integral of curl.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Choose flat surface",
    setup: "$C$: unit circle in $xy$-plane. Use Stokes with $S$ the unit disk.",
    steps: [
      { text: "Often curl flux on disk is easier than line integral.", why: "Carry the algebra carefully." },
      { text: "Compute $\\\\nabla\\\\times\\\\mathbf{F}$.", why: "Carry the algebra carefully." },
      { text: "Dot with $\\\\mathbf{k}\\\\,dA$.", why: "Carry the algebra carefully." },
      { text: "Integrate over disk.", why: "Carry the algebra carefully." },
      { text: "Orientation ccw from above.", why: "Carry the algebra carefully." },
      { text: "Same $C$ could bound a hemisphere \u2014 same answer if curl nice.", why: "Carry the algebra carefully." },
      { text: "Pick simplest $S$.", why: "Carry the algebra carefully." },
      { text: "State the strategy.", why: "Carry the algebra carefully." },
    ],
    result: "Prefer the flat disk spanning $C$",
    check: "Fewer trig terms.",
    mistake: "Parameterizing $C$ when disk is easy.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "Orientation RH rule",
    setup: "How to match $C$ and $S$ orientations.",
    steps: [
      { text: "Point thumb in normal direction on $S$.", why: "Carry the algebra carefully." },
      { text: "Fingers curl in positive $C$ direction.", why: "Carry the algebra carefully." },
      { text: "Inconsistent orientation flips sign.", why: "Carry the algebra carefully." },
      { text: "Exam checklist item.", why: "Carry the algebra carefully." },
      { text: "Draw a sketch.", why: "Carry the algebra carefully." },
      { text: "For graph $z=g(x,y)$, upward normal often default.", why: "Carry the algebra carefully." },
      { text: "State the rule.", why: "Carry the algebra carefully." },
      { text: "Apply once.", why: "Carry the algebra carefully." },
    ],
    result: "Right-hand consistency",
    check: "Sign errors are common.",
    mistake: "Ignoring orientation.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "Curl then integrate",
    setup: "$\\\\mathbf{F}=\\\\langle -y,x,0\\\\rangle$, $C$ unit circle ccw. Via Stokes.",
    steps: [
      { text: "$\\\\nabla\\\\times\\\\mathbf{F}=\\\\langle 0,0,2\\\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Disk normal $\\\\mathbf{k}$.", why: "Carry the algebra carefully." },
      { text: "$\\\\iint_D 2\\\\,dA=2\\\\pi$.", why: "Carry the algebra carefully." },
      { text: "Direct line integral also $2\\\\pi$.", why: "Carry the algebra carefully." },
      { text: "Match.", why: "Carry the algebra carefully." },
      { text: "Whirlpool circulation.", why: "Carry the algebra carefully." },
      { text: "Report $2\\\\pi$.", why: "Carry the algebra carefully." },
      { text: "Orientation check.", why: "Carry the algebra carefully." },
    ],
    result: "$2\\\\pi$",
    check: "Both methods agree.",
    mistake: "Using radius instead of area.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "Surface independence",
    setup: "Why can $S$ change if $\\\\partial S=C$ fixed?",
    steps: [
      { text: "If $\\\\mathrm{curl}\\\\,\\\\mathbf{F}$ has div $0$, closed surface flux of curl is $0$.", why: "Carry the algebra carefully." },
      { text: "Two surfaces with same boundary differ by a closed flux of curl $=0$.", why: "Carry the algebra carefully." },
      { text: "Hence same Stokes value.", why: "Carry the algebra carefully." },
      { text: "Choose convenient $S$.", why: "Carry the algebra carefully." },
      { text: "State the freedom.", why: "Carry the algebra carefully." },
      { text: "Need appropriate domain.", why: "Carry the algebra carefully." },
      { text: "Classic homework trick.", why: "Carry the algebra carefully." },
      { text: "Report the principle.", why: "Carry the algebra carefully." },
    ],
    result: "Same boundary $\\\\Rightarrow$ same circulation",
    check: "div curl $=0$ justifies deformation.",
    mistake: "Changing $C$ as well.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Reduce to Green",
    setup: "Show Stokes on $xy$-plane recovers Green.",
    steps: [
      { text: "$S$ in $z=0$, $d\\\\mathbf{S}=\\\\mathbf{k}\\\\,dA$.", why: "Carry the algebra carefully." },
      { text: "$(\\\\nabla\\\\times\\\\mathbf{F})\\\\cdot\\\\mathbf{k}=N_x-M_y$.", why: "Carry the algebra carefully." },
      { text: "Left side line integral in plane.", why: "Carry the algebra carefully." },
      { text: "Green's formula appears.", why: "Carry the algebra carefully." },
      { text: "Stokes generalizes Green.", why: "Carry the algebra carefully." },
      { text: "State reduction.", why: "Carry the algebra carefully." },
      { text: "Keep $P$ component if present (its curl parts).", why: "Carry the algebra carefully." },
      { text: "For $\\\\mathbf{F}=\\\\langle M,N,0\\\\rangle$ clean.", why: "Carry the algebra carefully." },
    ],
    result: "Green as planar Stokes",
    check: "Formulas match.",
    mistake: "Forgetting the $z$-components of curl.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "When line integral wins",
    setup: "When might direct $\\\\oint$ be easier?",
    steps: [
      { text: "When $C$ is simple and $\\\\mathbf{F}$ restricts nicely.", why: "Carry the algebra carefully." },
      { text: "When curl is messy.", why: "Carry the algebra carefully." },
      { text: "When surface hard to describe.", why: "Carry the algebra carefully." },
      { text: "Compare before committing.", why: "Carry the algebra carefully." },
      { text: "State heuristic.", why: "Carry the algebra carefully." },
      { text: "Conservative $\\\\Rightarrow$ closed integral $0$ instantly.", why: "Carry the algebra carefully." },
      { text: "Check curl zero first.", why: "Carry the algebra carefully." },
      { text: "Report decision rule.", why: "Carry the algebra carefully." },
    ],
    result: "Pick the easier side of Stokes",
    check: "Always compute curl early.",
    mistake: "Forcing a bad surface.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Hypotheses",
    setup: "List hypotheses for Stokes.",
    steps: [
      { text: "Oriented piecewise smooth surface.", why: "Carry the algebra carefully." },
      { text: "Piecewise smooth oriented boundary.", why: "Carry the algebra carefully." },
      { text: "$\\\\mathbf{F}$ continuously differentiable on an open set containing $S$.", why: "Carry the algebra carefully." },
      { text: "Orientation consistency.", why: "Carry the algebra carefully." },
      { text: "State them briefly.", why: "Carry the algebra carefully." },
      { text: "Counterexamples if violated.", why: "Carry the algebra carefully." },
      { text: "Exam: mention smoothness.", why: "Carry the algebra carefully." },
      { text: "Then apply.", why: "Carry the algebra carefully." },
    ],
    result: "Smooth $S$, $\\\\partial S$, and $\\\\mathbf{F}$",
    check: "Standard textbook list.",
    mistake: "Applying on a field with a singularity on $S$.",
  },
];

export const STOKES_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Hemisphere vs disk",
    setup: "$C$ unit circle. Compare Stokes on hemisphere vs disk for $\\\\mathbf{F}=\\\\langle -y,x,0\\\\rangle$.",
    steps: [
      { text: "Curl $=\\\\langle 0,0,2\\\\rangle$.", why: "Carry the algebra carefully." },
      { text: "Disk: $2\\\\cdot\\\\mathrm{area}=2\\\\pi$.", why: "Carry the algebra carefully." },
      { text: "Hemisphere: $(\\\\nabla\\\\times\\\\mathbf{F})\\\\cdot\\\\mathbf{n}$ integrates to same $2\\\\pi$.", why: "Carry the algebra carefully." },
      { text: "Boundary identical.", why: "Carry the algebra carefully." },
      { text: "Disk is less work.", why: "Carry the algebra carefully." },
      { text: "Report both equal $2\\\\pi$.", why: "Carry the algebra carefully." },
      { text: "Orientation upward/outward consistent with ccw $C$.", why: "Carry the algebra carefully." },
      { text: "State equality.", why: "Carry the algebra carefully." },
    ],
    result: "Both give $2\\\\pi$",
    check: "Surface independence.",
    mistake: "Getting different answers from orientation mismatch.",
    life: "Changing a spanning surface without changing the wire loop is a standard lab move \u2014 Stokes guarantees the circulation reading stays the same when curl is well-behaved.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Workflow drill",
    setup: "List a 6-step Stokes workflow.",
    steps: [
      { text: "Identify $C$ and choose $S$.", why: "Carry the algebra carefully." },
      { text: "Check orientation.", why: "Carry the algebra carefully." },
      { text: "Compute $\\\\nabla\\\\times\\\\mathbf{F}$.", why: "Carry the algebra carefully." },
      { text: "Find $\\\\mathbf{n}\\\\,dS$ or project.", why: "Carry the algebra carefully." },
      { text: "Integrate.", why: "Carry the algebra carefully." },
      { text: "Sanity-check magnitude/sign.", why: "Carry the algebra carefully." },
      { text: "Optional: spot-check with direct line integral.", why: "Carry the algebra carefully." },
      { text: "Box the answer.", why: "Carry the algebra carefully." },
    ],
    result: "Choose $S$ \u2192 curl \u2192 integrate",
    check: "Prevents wandering algebra.",
    mistake: "Integrating before computing curl.",
  },
  {
    number: 3,
    tier: "Basic",
    title: "Projection formula",
    setup: "For graph $z=g(x,y)$, write $(\\\\nabla\\\\times\\\\mathbf{F})\\\\cdot d\\\\mathbf{S}$.",
    steps: [
      { text: "$d\\\\mathbf{S}=(-g_x,-g_y,1)\\\\,dx\\\\,dy$ (upward).", why: "Carry the algebra carefully." },
      { text: "Dot with curl.", why: "Carry the algebra carefully." },
      { text: "Integrate over shadow $D$.", why: "Carry the algebra carefully." },
      { text: "Common exam setup.", why: "Carry the algebra carefully." },
      { text: "Watch the sign of normal.", why: "Carry the algebra carefully." },
      { text: "State formula.", why: "Carry the algebra carefully." },
      { text: "Apply on a plane $z=ax+by+c$.", why: "Carry the algebra carefully." },
      { text: "Report projected integrand.", why: "Carry the algebra carefully." },
    ],
    result: "$(-g_x,-g_y,1)$ projection",
    check: "Reduces to double integral on $D$.",
    mistake: "Using $(g_x,g_y,-1)$ without flipping orientation.",
  },
  {
    number: 4,
    tier: "Intermediate",
    title: "Zero curl case",
    setup: "If $\\\\nabla\\\\times\\\\mathbf{F}=\\\\mathbf{0}$ on simply connected set, Stokes says?",
    steps: [
      { text: "Every closed circulation is $0$.", why: "Carry the algebra carefully." },
      { text: "Path independence.", why: "Carry the algebra carefully." },
      { text: "Potential exists.", why: "Carry the algebra carefully." },
      { text: "Same conclusion as conservative tests.", why: "Carry the algebra carefully." },
      { text: "Stokes is the integral view.", why: "Carry the algebra carefully." },
      { text: "State $0$.", why: "Carry the algebra carefully." },
      { text: "Domain matters.", why: "Carry the algebra carefully." },
      { text: "Tie to earlier chapters.", why: "Carry the algebra carefully." },
    ],
    result: "Closed integrals vanish",
    check: "Consistent with FTC for line integrals.",
    mistake: "Ignoring holes in the domain.",
  },
  {
    number: 5,
    tier: "Intermediate",
    title: "Piecewise boundary",
    setup: "Stokes with a triangular boundary \u2014 tips.",
    steps: [
      { text: "Can integrate on three segments OR span with flat triangle surface.", why: "Carry the algebra carefully." },
      { text: "Flat surface usually wins.", why: "Carry the algebra carefully." },
      { text: "Curl flux over the triangular region.", why: "Carry the algebra carefully." },
      { text: "Orientation around the triangle.", why: "Carry the algebra carefully." },
      { text: "Corners OK if piecewise smooth.", why: "Carry the algebra carefully." },
      { text: "State preference.", why: "Carry the algebra carefully." },
      { text: "Compute area vectors if planar.", why: "Carry the algebra carefully." },
      { text: "Report strategy.", why: "Carry the algebra carefully." },
    ],
    result: "Prefer planar spanning surface",
    check: "Three line integrals are longer.",
    mistake: "Missing a side of $C$.",
  },
  {
    number: 6,
    tier: "Intermediate",
    title: "Common sign bug",
    setup: "Name two frequent Stokes sign errors.",
    steps: [
      { text: "Normal pointing opposite the RH rule.", why: "Carry the algebra carefully." },
      { text: "Clockwise $C$ with upward normal.", why: "Carry the algebra carefully." },
      { text: "Fix: redraw and reapply RH.", why: "Carry the algebra carefully." },
      { text: "Or multiply final answer by $-1$ if consistently flipped.", why: "Carry the algebra carefully." },
      { text: "State both bugs.", why: "Carry the algebra carefully." },
      { text: "Prevention: sketch.", why: "Carry the algebra carefully." },
      { text: "Exam partial credit for orientation note.", why: "Carry the algebra carefully." },
      { text: "Report awareness.", why: "Carry the algebra carefully." },
    ],
    result: "Orientation mismatches",
    check: "Sketch saves marks.",
    mistake: "Never mentioning orientation.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Maxwell vignette",
    setup: "How does Stokes appear in Faraday's law sketches?",
    steps: [
      { text: "Circulation of $\\\\mathbf{E}$ around a loop related to changing $\\\\mathbf{B}$ flux.", why: "Carry the algebra carefully." },
      { text: "Integral form resembles Stokes structure.", why: "Carry the algebra carefully." },
      { text: "Curl $\\\\mathbf{E}$ linked to $-\\\\partial_t\\\\mathbf{B}$.", why: "Carry the algebra carefully." },
      { text: "Motivation, not a full EM course.", why: "Carry the algebra carefully." },
      { text: "State the analogy.", why: "Carry the algebra carefully." },
      { text: "Math skill transfers.", why: "Carry the algebra carefully." },
      { text: "Keep claims modest.", why: "Carry the algebra carefully." },
      { text: "Point to the shared pattern.", why: "Carry the algebra carefully." },
    ],
    result: "Loop circulation \u2194 field curl/flux",
    check: "Same geometric theorem.",
    mistake: "Overclaiming EM details.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Capstone compare",
    setup: "Compare Green, Stokes, and Divergence in one sentence each.",
    steps: [
      { text: "Green: planar circulation \u2194 area integral of 2D curl.", why: "Carry the algebra carefully." },
      { text: "Stokes: space circulation \u2194 surface curl flux.", why: "Carry the algebra carefully." },
      { text: "Divergence: closed flux \u2194 volume integral of div.", why: "Carry the algebra carefully." },
      { text: "All: boundary data \u2194 interior operator.", why: "Carry the algebra carefully." },
      { text: "Choose by dimension of the 'boundary'.", why: "Carry the algebra carefully." },
      { text: "State the trio.", why: "Carry the algebra carefully." },
      { text: "Pick the matching theorem on exams.", why: "Carry the algebra carefully." },
      { text: "Report the comparison.", why: "Carry the algebra carefully." },
    ],
    result: "Boundary \u2194 interior for curl/div",
    check: "Dimension dictates the theorem.",
    mistake: "Using divergence theorem for a line integral.",
  },
];
